/**
 * ZenPlaygroundOptimized - Unified zen experience with embedded processing
 * 
 * Philosophy:
 * - Single zen interface throughout (no technical interruptions)
 * - Meditative processing flow using nature metaphors
 * - Live lens creation within zen aesthetics
 * - Seamless input → generate → edit experience
 * - 89% token optimization hidden behind zen messaging
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { parseCode, ParserConfig } from '../utils/unifiedParser';
import { enumerateLensAnchors, deduplicateLensAnchors } from '../utils/lensAnchorEnumerator';
import { LensAnchorDescriptor, AnchorSelection, LensletContent, AIObservabilityData, EnhancedDebugStep } from '../types/lensAnchorTypes';
import { ASTLine, ASTToken } from '../utils/treeSitterParser';
import { TreeSitterQueryEngine, SemanticMatch } from '../utils/treeSitterQueryEngine';
import { selectAnchorsFromLLM, calculateTokenSavings } from '../services/codeFirstSelection';
import { generateLensletContent } from '../services/lensAnchorService';
import { useZenSettings } from '../hooks/useZenSettings';
import { useProviders } from '../hooks/useProviders';
import { 
  llmLogger, 
  DebugErrorBoundary, 
  DebugPanelEnhanced,
  observabilityService,
  performanceMonitor,
  timeOperation
} from '../debug';
import { LensPicker, type Lens } from './LensPicker';
import '../styles/ZenPlayground.css';

interface ZenPhase {
  id: string;
  phase: 'input' | 'generating' | 'live-creation' | 'editing';
  meditation: {
    message: string;
    emoji: string;
    progress: number;
  };
  completedLenses: string[];
  generatingLenses: string[];
}

// AI Debug Analysis Interface
interface DebugAnalysis {
  timestamp: number;
  step: string;
  issue: string;
  evidence: any;
  recommendation: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

// Network Traffic Monitor
interface NetworkCall {
  id: string;
  url: string;
  method: string;
  status: number | null;
  requestTime: number;
  responseTime: number | null;
  requestBody?: any;
  responseBody?: any;
  error?: string;
}

// Real-time State Monitor
interface StateSnapshot {
  timestamp: number;
  step: string;
  state: {
    phase: string;
    anchorsFound: number;
    selectedAnchors: number;
    zenLenses: number;
    windowFallback?: any;
    reactState?: any;
  };
  memoryUsage?: any;
}

interface ZenLens {
  id: string;
  active: boolean;
  line: number;
  text: string;
  syntax: {
    from: string;
    to: string;
    hint: string;
  };
  concept: {
    title: string;
    description: string;
    points: {
      known: string[];
      target: string[];
    };
  };
  paradigm: {
    title: string;
    explanation: string;
    insights: string[];
  };
  tempId: string;
  novelty: number;
}

interface OptimizationStats {
  tokensSaved: number;
  responseTime: number;
  matchAccuracy: number;
  originalTokens: number;
  optimizedTokens: number;
}

interface DebugStep {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  startTime?: number;
  endTime?: number;
  duration?: number;
  input?: any;
  output?: any;
  error?: string;
  analytics?: {
    performance?: { [key: string]: any };
    data?: { [key: string]: any };
    quality?: { [key: string]: any };
  };
}

// Available Lenses for Generation (Legacy structure for backward compatibility)
const AVAILABLE_LENSES = {
  standard: {
    name: 'Standard',
    lenses: [
      { id: 'LENS_STANDARD_DEFAULT', name: 'Standard Lens', description: 'Clear educational explanations' },
      { id: 'LENS_COMPACT_DEFAULT', name: 'Compact Lens', description: 'Minimal tokens, maximum clarity' }
    ]
  },
  persona: {
    name: 'Learning Styles',
    lenses: [
      { id: 'LENS_PERSONA_BEGINNER', name: 'Beginner Friendly', description: 'Simple explanations for newcomers' },
      { id: 'LENS_PERSONA_EXPERT', name: 'Expert Technical', description: 'Deep technical details' },
      { id: 'LENS_PERSONA_STORYTELLER', name: 'Story-based', description: 'Learn through narratives' },
      { id: 'LENS_PERSONA_VISUAL', name: 'Visual Learner', description: 'Visual metaphors and diagrams' }
    ]
  },
  language: {
    name: 'Languages',
    lenses: [
      { id: 'LENS_LANGUAGE_TELUGU', name: 'Telugu', description: 'తెలుగులో వివరణలు' }
    ]
  },
  technical: {
    name: 'Technical Domains',
    lenses: [
      { id: 'LENS_TECHNICAL_WEB', name: 'Web Developer', description: 'Frontend/backend perspective' },
      { id: 'LENS_TECHNICAL_GAMEDEV', name: 'Game Developer', description: 'Real-time systems perspective' }
    ]
  }
};

// Convert to LensPicker format
const LENS_PICKER_DATA: Lens[] = [
  // Standard lenses
  {
    id: 'LENS_STANDARD_DEFAULT',
    title: 'Standard Lens — Clear educational explanations',
    short: 'Balanced clarity + details',
    category: 'Standard',
    tags: ['balanced', 'educational', 'default'],
    exampleIn: 'Memory Management (Box<T>)',
    preview: 'Maps C malloc/free to Rust ownership + Box<T>. Explains move semantics and why explicit return isn\'t needed when last expression is the value.'
  },
  {
    id: 'LENS_COMPACT_DEFAULT',
    title: 'Compact Lens — Minimal tokens, maximum clarity',
    short: 'Short, dense, essential',
    category: 'Standard',
    tags: ['concise', 'budget', 'fast'],
    exampleIn: 'Pointers → References',
    preview: 'Bullet mapping with tight phrasing, focuses on do/don\'t and 1 code micro-example.'
  },
  // Learning Style lenses
  {
    id: 'LENS_PERSONA_BEGINNER',
    title: 'Beginner Friendly — Simple explanations for newcomers',
    short: 'Plain language, gentle pacing',
    category: 'Learning Style',
    tags: ['beginner', 'plain-language', 'step-by-step'],
    exampleIn: 'Struct vs Class mental model',
    preview: 'Avoids jargon, adds tiny analogies, one pitfall called out simply.'
  },
  {
    id: 'LENS_PERSONA_EXPERT',
    title: 'Expert Technical — Deep technical details',
    short: 'Precise, low-level, rigorous',
    category: 'Learning Style',
    tags: ['advanced', 'internals', 'precise'],
    exampleIn: 'Lifetimes demystified',
    preview: 'Mentions borrow checker phases, elision rules, MIR hints, and unsafe trade-offs.'
  },
  {
    id: 'LENS_PERSONA_STORYTELLER',
    title: 'Story-based — Learn through narratives',
    short: 'Narrative arc, memorable',
    category: 'Learning Style',
    tags: ['metaphor', 'engaging'],
    exampleIn: 'Ownership as library card',
    preview: 'Tiny story that maps borrowing to a library checkout system; ends with a moral/gotcha.'
  },
  {
    id: 'LENS_PERSONA_VISUAL',
    title: 'Visual Learner — Visual metaphors and diagrams',
    short: 'Diagram-first mental model',
    category: 'Learning Style',
    tags: ['visual', 'diagram', 'blocks'],
    exampleIn: 'Stack vs Heap sketch',
    preview: 'Outputs ASCII diagram blocks and arrows; minimal prose, high signal visuals.'
  },
  // Language lenses
  {
    id: 'LENS_LANGUAGE_TELUGU',
    title: 'Telugu — తెలుగులో వివరణలు',
    short: 'Bilingual hints where useful',
    category: 'Language',
    tags: ['telugu', 'bilingual', 'accessible'],
    exampleIn: 'Ownership basics',
    preview: 'C → Rust mapping with Telugu side notes for tricky parts (అలవాటు vs నియమం).'
  },
  // Domain lenses
  {
    id: 'LENS_TECHNICAL_WEB',
    title: 'Web Developer — Frontend/Backend perspective',
    short: 'Relates to web runtime concerns',
    category: 'Domain',
    tags: ['web', 'perf', 'tooling'],
    exampleIn: 'Async & await mental model',
    preview: 'Connects to event loops, latency budgets, and API boundaries.'
  },
  {
    id: 'LENS_TECHNICAL_GAMEDEV',
    title: 'Game Developer — Real‑time systems perspective',
    short: 'Frame budgets, ECS angle',
    category: 'Domain',
    tags: ['gamedev', 'realtime', 'ecs'],
    exampleIn: 'Borrow rules in ECS',
    preview: 'Talks cache friendliness, fixed‑step loops, and aliasing hazards.'
  }
];

// C→Rust Code Examples for Testing Improved Prompts
const C_TO_RUST_EXAMPLES = {
  memory: {
    title: "Memory Management",
    description: "Box<T>, ownership, and heap allocation",
    code: `fn create_user() -> Box<User> {
    let user = Box::new(User {
        name: String::from("Alice"),
        age: 30,
    });
    user  // No "return" needed
}`
  },
  
  errorHandling: {
    title: "Error Handling", 
    description: "Result<T, E> and error propagation",
    code: `fn divide_numbers(a: f64, b: f64) -> Result<f64, String> {
    if b == 0.0 {
        return Err("Cannot divide by zero".to_string());
    }
    
    if a.is_nan() || b.is_nan() {
        return Err("Invalid number input".to_string());
    }
    
    Ok(a / b)
}`
  },
  
  collections: {
    title: "Collections & Iterators",
    description: "Vec<T>, HashMap, and functional programming",
    code: `fn process_items(items: Vec<&str>) -> HashMap<String, usize> {
    items
        .iter()
        .filter(|s| s.len() > 3)
        .map(|s| s.to_uppercase())
        .enumerate()
        .collect::<HashMap<String, usize>>()
}`
  }
};

const C_TO_GO_EXAMPLES = {
  memory: {
    title: "Memory & Pointers",
    description: "Pointer management and struct allocation in Go",
    code: `func createUser() *User {
    user := &User{
        Name: "Alice",
        Age:  30,
    }
    return user  // Explicit return required
}`
  },
  
  errorHandling: {
    title: "Error Handling",
    description: "Go's idiomatic error handling pattern",
    code: `func divideNumbers(a, b float64) (float64, error) {
    if b == 0.0 {
        return 0, fmt.Errorf("cannot divide by zero")
    }
    
    if math.IsNaN(a) || math.IsNaN(b) {
        return 0, fmt.Errorf("invalid number input")
    }
    
    return a / b, nil
}`
  },
  
  concurrency: {
    title: "Goroutines & Channels",
    description: "Concurrent programming with goroutines and channels",
    code: `func processItems(items []string) <-chan Result {
    results := make(chan Result, len(items))
    
    for _, item := range items {
        go func(s string) {
            // Process in parallel
            result := process(s)
            results <- result
        }(item)
    }
    
    return results
}`
  }
};

const ZenPlaygroundCore: React.FC = () => {
  const [courseContext] = useState({
    known: 'C',
    target: 'Rust',
    topic: 'Memory Management'
  });
  
  // Zen settings for provider selection
  const { getPreferredProvider } = useZenSettings();
  const { providers } = useProviders();
  
  // Parser initialization state
  const [parserReady, setParserReady] = useState(false);
  const parserInitRef = useRef(false);

  const [knownLanguage, setKnownLanguage] = useState('C');
  const [targetLanguage, setTargetLanguage] = useState('Rust');
  const [selectedExample, setSelectedExample] = useState('memory');
  
  // Initialize code based on target language
  const getInitialCode = () => {
    const examples = targetLanguage === 'Go' ? C_TO_GO_EXAMPLES : C_TO_RUST_EXAMPLES;
    return examples[selectedExample as keyof typeof examples]?.code || '';
  };
  
  const [code, setCode] = useState(getInitialCode());
  
  // Lens selection state
  const [selectedLens, setSelectedLens] = useState('LENS_STANDARD_DEFAULT');
  const [customLensInstructions, setCustomLensInstructions] = useState('');

  // Zen processing state
  const [zenPhase, setZenPhase] = useState<ZenPhase>({
    id: 'zen-flow',
    phase: 'input',
    meditation: {
      message: 'Share your code to begin the journey...',
      emoji: '🌱',
      progress: 0
    },
    completedLenses: [],
    generatingLenses: []
  });

  const [isGenerating, setIsGenerating] = useState(false);

  // Results state
  const [parseResult, setParseResult] = useState<any>(null);
  const [anchors, setAnchors] = useState<LensAnchorDescriptor[]>([]);
  const [selectedAnchors, setSelectedAnchors] = useState<AnchorSelection[]>([]);
  const [, setGeneratedLenslets] = useState<LensletContent[]>([]);
  const [optimizationStats, setOptimizationStats] = useState<OptimizationStats | null>(null);

  // Zen UI state
  const [zenLenses, setZenLenses] = useState<ZenLens[]>([]);
  const [activeLensId, setActiveLensId] = useState<string>('');
  const [bloomingLenses, setBloomingLenses] = useState<Set<string>>(new Set());
  const [showHighlightDebug, setShowHighlightDebug] = useState(false);

  // Debug panel state
  const [debugPanelVisible, setDebugPanelVisible] = useState(false);
  const [actionsMenuExpanded, setActionsMenuExpanded] = useState(false);
  const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>('top');
  const actionsToggleRef = useRef<HTMLButtonElement>(null);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const [debugSteps, setDebugSteps] = useState<DebugStep[]>([
    { id: 'parse', name: 'Parse Code', status: 'pending' },
    { id: 'semantic', name: 'LLM Semantic Picks', status: 'pending' },
    { id: 'resolve', name: 'Validate with AST', status: 'pending' },
    { id: 'generate', name: 'Generate Lenses', status: 'pending' }
  ]);
  const [debugMode, setDebugMode] = useState<'basic' | 'advanced' | 'ai-powered'>('ai-powered');
  const [autoDebug, setAutoDebug] = useState(true);

  // AI Observability state
  const [aiObservabilityData, setAiObservabilityData] = useState<Map<string, AIObservabilityData>>(new Map());

  // Advanced Debug System
  const [debugAnalyses, setDebugAnalyses] = useState<DebugAnalysis[]>([]);
  const [networkCalls, setNetworkCalls] = useState<NetworkCall[]>([]);
  const [stateSnapshots, setStateSnapshots] = useState<StateSnapshot[]>([]);
  const [aiDebugEnabled, setAiDebugEnabled] = useState(true);
  const [realTimeMonitoring, setRealTimeMonitoring] = useState(true);
  const debugIntervalRef = useRef<number | null>(null);
  const networkInterceptorRef = useRef<any>(null);

  // Update code when target language or example changes
  useEffect(() => {
    const examples = targetLanguage === 'Go' ? C_TO_GO_EXAMPLES : C_TO_RUST_EXAMPLES;
    const example = examples[selectedExample as keyof typeof examples];
    
    // If the selected example doesn't exist in the new language, default to the first example
    if (!example) {
      const firstExampleKey = Object.keys(examples)[0];
      setSelectedExample(firstExampleKey);
      setCode(examples[firstExampleKey as keyof typeof examples].code);
    } else {
      setCode(example.code);
    }
  }, [targetLanguage]);

  // Initialize tree-sitter parser on mount
  useEffect(() => {
    const initializeParser = async () => {
      if (parserInitRef.current) return;
      parserInitRef.current = true;
      
      console.log('🌳 Initializing tree-sitter parser...');
      try {
        // Warm up the parser with a simple code snippet
        const testCode = 'fn main() {}';
        const result = await parseCode({
          code: testCode,
          knownLanguage: 'C',
          targetLanguage: 'Rust',
          lenses: {}
        }, {
          preferredMethod: 'tree-sitter',
          enableFallback: false,
          timeoutMs: 5000
        });
        
        if (result.success) {
          console.log('✅ Tree-sitter parser ready!');
          setParserReady(true);
        } else {
          console.warn('⚠️ Parser initialization test failed, but marking as ready');
          setParserReady(true); // Still mark as ready, will retry on actual use
        }
      } catch (error) {
        console.warn('⚠️ Parser initialization error:', error);
        // Mark as ready anyway - the retry logic in parseCodeStep will handle failures
        setTimeout(() => setParserReady(true), 1000);
      }
    };
    
    initializeParser();
  }, []);
  
  // Real-time monitoring setup
  useEffect(() => {
    if (realTimeMonitoring) {
      debugIntervalRef.current = window.setInterval(() => {
        captureStateSnapshot('realtime_monitor');
        analyzeCurrentState();
      }, 2000);
    }
    
    // Network interceptor setup
    setupNetworkInterceptor();
    
    return () => {
      if (debugIntervalRef.current) {
        window.clearInterval(debugIntervalRef.current);
      }
      cleanupNetworkInterceptor();
    };
  }, [realTimeMonitoring]);

  // Data transformation: LensletContent -> ZenLens
  const transformToZenLenses = (
    lenslets: LensletContent[], 
    anchors: LensAnchorDescriptor[], 
    selections: AnchorSelection[]
  ): ZenLens[] => {
    return lenslets.map((lenslet, index) => {
      const anchor = anchors.find(a => a.tempId === lenslet.tempId);
      const selection = selections.find(s => s.tempId === lenslet.tempId);
      
      // Debug: Log the matching process
      if (index === 0) {
        console.log('🔄 Transform Debug:', {
          lensletTempId: lenslet.tempId,
          anchorFound: !!anchor,
          anchorText: anchor?.text,
          selectionFound: !!selection,
          selectionText: selection?.text,
          availableSelectionTempIds: selections.map(s => s.tempId),
          availableAnchorTempIds: anchors.map(a => a.tempId)
        });
      }
      
      // Additional tempId tracking
      console.log(`🔍 TempId Flow [Lens ${index}]:`, {
        input_lensletTempId: lenslet.tempId,
        output_lensTempId: lenslet.tempId,
        matched_anchorTempId: anchor?.tempId,
        matched_selectionTempId: selection?.tempId,
        allMatch: lenslet.tempId === anchor?.tempId && lenslet.tempId === selection?.tempId
      })
      
      // Extract line number from anchor span
      const line = anchor?.span?.startPoint?.row ? anchor.span.startPoint.row + 1 : index + 1;
      
      // Parse surface content for syntax mapping
      const surface = lenslet.lenslet.content.surface || '';
      
      // Multiple parsing strategies for different formats
      let fromSyntax = 'Unknown';
      let toSyntax = 'Target syntax';
      
      // Try format: "C: code → Rust: code"
      const labeledMatch = surface.match(/C:\s*([^→]+)\s*→\s*Rust:\s*(.+)/);
      if (labeledMatch) {
        fromSyntax = labeledMatch[1].trim();
        toSyntax = labeledMatch[2].trim();
      } else {
        // Try format: "code → code" (without labels)
        const arrowMatch = surface.match(/([^→]+)→(.+)/);
        if (arrowMatch) {
          fromSyntax = arrowMatch[1].trim();
          toSyntax = arrowMatch[2].trim();
        } else {
          // Try format with backticks: "`code` → `code`"
          const backtickMatch = surface.match(/`([^`]+)`.*?`([^`]+)`/);
          if (backtickMatch) {
            fromSyntax = backtickMatch[1];
            toSyntax = backtickMatch[2];
          }
        }
      }
      
      return {
        id: lenslet.tempId,
        active: index === 0,
        line,
        text: selection?.text || anchor?.text || (lenslet.tempId.includes('llm_') ? 'Selection' : 'Line ' + line),
        tempId: lenslet.tempId,
        novelty: selection?.novelty || 0,
        syntax: {
          from: fromSyntax || anchor?.text || 'Unknown',
          to: toSyntax,
          hint: lenslet.lenslet.content.badge || 'Code pattern'
        },
        concept: {
          title: extractTitle(lenslet.lenslet.content.concept) || 'Concept Difference',
          description: lenslet.lenslet.content.concept || 'Understanding the difference...',
          points: {
            known: extractPoints(lenslet.lenslet.content.concept, knownLanguage),
            target: extractPoints(lenslet.lenslet.content.concept, targetLanguage)
          }
        },
        paradigm: {
          title: extractTitle(lenslet.lenslet.content.paradigm) || 'Paradigm Shift',
          explanation: lenslet.lenslet.content.paradigm || 'Deeper philosophical difference...',
          insights: extractInsights(lenslet.lenslet.content.paradigm)
        }
      };
    });
  };

  // Helper functions for parsing generated content
  const extractTitle = (content: string): string => {
    const match = content.match(/^([^.\n]+)/);
    return match?.[1]?.trim() || '';
  };

  const extractPoints = (content: string, language: string): string[] => {
    const points = content.match(new RegExp(`${language}[^:]*:([^${language === 'C' ? 'Rust' : 'C'}]*)`));
    if (!points) return [`${language} approach`, 'Key characteristics', 'Important features'];
    
    return points[1]
      .split(/[•\-\n]/)
      .map(p => p.trim())
      .filter(p => p.length > 5)
      .slice(0, 3);
  };

  const extractInsights = (content: string): string[] => {
    const insights = content.match(/💡|🛡️|🔄|🎯|📝|🔒/g);
    if (!insights) return ['💡 Key insight', '🛡️ Safety improvement', '🚀 Performance benefit'];
    
    const sentences = content.split(/[.!]/).filter(s => s.trim().length > 10);
    return sentences.slice(0, 3).map((s, i) => {
      const emoji = ['💡', '🛡️', '🚀'][i % 3];
      return `${emoji} ${s.trim()}`;
    });
  };


  // Zen meditation messages
  const zenMessages = {
    parse: { emoji: '🌱', message: 'Understanding your code structure...' },
    enumerate: { emoji: '🌸', message: 'Discovering learning opportunities...' },
    select: { emoji: '✨', message: 'Selecting the most illuminating insights...' },
    match: { emoji: '🎋', message: 'Connecting wisdom to precise locations...' },
    generate: { emoji: '🌊', message: 'Growing explanations with care...' },
    complete: { emoji: '🌙', message: 'Your lens garden is ready to explore...' }
  };

  // Update zen meditation helper
  const updateZenMeditation = (phase: ZenPhase['phase'], emoji: string, message: string, progress: number) => {
    setZenPhase(prev => ({
      ...prev,
      phase,
      meditation: { emoji, message, progress }
    }));
  };

  // Handle example selection based on target language
  const selectCodeExample = (exampleKey: string) => {
    const examples = targetLanguage === 'Go' ? C_TO_GO_EXAMPLES : C_TO_RUST_EXAMPLES;
    const example = examples[exampleKey as keyof typeof examples];
    if (example) {
      setCode(example.code);
      setSelectedExample(exampleKey);
      // Reset zen state when switching examples
      setZenPhase({
        id: 'zen-flow',
        phase: 'input',
        meditation: { emoji: '🌱', message: 'Ready to discover new insights...', progress: 0 },
        completedLenses: [],
        generatingLenses: []
      });
    }
  };

  // Debug helpers
  const startDebugStep = (stepId: string, input?: any) => {
    setDebugSteps(prev => prev.map(step => 
      step.id === stepId 
        ? { ...step, status: 'running', startTime: performance.now(), input }
        : step
    ));
  };

  const completeDebugStep = (stepId: string, output?: any, analytics?: DebugStep['analytics']) => {
    setDebugSteps(prev => prev.map(step => {
      if (step.id === stepId) {
        const endTime = performance.now();
        return {
          ...step, 
          status: 'completed', 
          endTime, 
          duration: step.startTime ? endTime - step.startTime : undefined,
          output,
          analytics
        };
      }
      return step;
    }));
  };

  const errorDebugStep = (stepId: string, error: string) => {
    setDebugSteps(prev => prev.map(step => 
      step.id === stepId 
        ? { ...step, status: 'error', endTime: performance.now(), error }
        : step
    ));
  };

  // AI-Powered Debug System
  const captureStateSnapshot = (step: string) => {
    const snapshot: StateSnapshot = {
      timestamp: Date.now(),
      step,
      state: {
        phase: zenPhase.phase,
        anchorsFound: anchors.length,
        selectedAnchors: selectedAnchors.length,
        zenLenses: zenLenses.length,
        windowFallback: (window as any).__directSelections,
        reactState: {
          isGenerating,
          parseResult: !!parseResult,
          optimizationStats
        }
      },
      memoryUsage: (performance as any).memory ? {
        usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
        totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit
      } : undefined
    };
    
    setStateSnapshots(prev => [...prev.slice(-19), snapshot]);
    return snapshot;
  };

  const analyzeCurrentState = async () => {
    if (!aiDebugEnabled) return;
    
    const currentState = captureStateSnapshot('ai_analysis');
    const recentSnapshots = stateSnapshots.slice(-5);
    const recentNetworkCalls = networkCalls.slice(-10);
    
    // Detect common issues
    const analyses: DebugAnalysis[] = [];
    
    // Issue 1: State timing problems
    if (currentState.state.selectedAnchors === 0 && currentState.state.anchorsFound > 0) {
      analyses.push({
        timestamp: Date.now(),
        step: 'state_analysis',
        issue: 'Selected anchors are 0 despite found anchors',
        evidence: {
          anchorsFound: currentState.state.anchorsFound,
          selectedAnchors: currentState.state.selectedAnchors,
          windowFallback: currentState.state.windowFallback
        },
        recommendation: 'Check React state timing. Use window fallback if needed.',
        severity: 'high'
      });
    }
    
    // Issue 2: Network failures
    const failedCalls = recentNetworkCalls.filter(call => call.status && call.status >= 400);
    if (failedCalls.length > 0) {
      analyses.push({
        timestamp: Date.now(),
        step: 'network_analysis',
        issue: 'API calls failing',
        evidence: failedCalls,
        recommendation: 'Check backend server status and API endpoints',
        severity: 'critical'
      });
    }
    
    // Issue 3: Memory leaks
    if (currentState.memoryUsage && recentSnapshots.length > 2) {
      const memoryGrowth = currentState.memoryUsage.usedJSHeapSize - 
        (recentSnapshots[0].memoryUsage?.usedJSHeapSize || 0);
      if (memoryGrowth > 50 * 1024 * 1024) { // 50MB growth
        analyses.push({
          timestamp: Date.now(),
          step: 'memory_analysis',
          issue: 'Potential memory leak detected',
          evidence: { memoryGrowth, currentUsage: currentState.memoryUsage },
          recommendation: 'Check for unreleased event listeners or large object accumulation',
          severity: 'medium'
        });
      }
    }
    
    if (analyses.length > 0) {
      setDebugAnalyses(prev => [...prev.slice(-49), ...analyses]);
    }
  };

  const setupNetworkInterceptor = () => {
    const originalFetch = window.fetch;
    window.fetch = async (input, init) => {
      const callId = `fetch_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      const url = typeof input === 'string' ? input : (input as Request).url;
      const method = init?.method || 'GET';
      const requestTime = performance.now();
      
      const networkCall: NetworkCall = {
        id: callId,
        url,
        method,
        status: null,
        requestTime,
        responseTime: null,
        requestBody: init?.body
      };
      
      setNetworkCalls(prev => [...prev.slice(-49), networkCall]);
      
      try {
        const response = await originalFetch(input, init);
        const responseTime = performance.now();
        
        // Try to capture response body (filter binary content)
        let responseBody;
        try {
          const contentType = response.headers.get('content-type') || '';
          const clonedResponse = response.clone();
          
          // Skip binary content types
          if (contentType.includes('application/wasm') ||
              contentType.includes('application/octet-stream') ||
              url.endsWith('.wasm') ||
              url.includes('tree-sitter')) {
            responseBody = `[Binary content: ${contentType || 'WASM/Binary'}]`;
          } else {
            const text = await clonedResponse.text();
            // Check if response contains mostly binary data
            if (isBinaryContent(text)) {
              responseBody = `[Binary content detected: ${text.length} bytes]`;
            } else {
              // Limit response size for readability
              responseBody = text.length > 1000 ? 
                text.substring(0, 1000) + '...[truncated]' : text;
            }
          }
        } catch {
          responseBody = 'Could not capture response body';
        }
        
        setNetworkCalls(prev => prev.map(call => 
          call.id === callId 
            ? { ...call, status: response.status, responseTime, responseBody }
            : call
        ));
        
        return response;
      } catch (error) {
        const responseTime = performance.now();
        setNetworkCalls(prev => prev.map(call => 
          call.id === callId 
            ? { ...call, status: 0, responseTime, error: error.message }
            : call
        ));
        throw error as Error;
      }
    };
  };

  const cleanupNetworkInterceptor = () => {
    // Reset to original fetch if we stored it
    if (networkInterceptorRef.current) {
      window.fetch = networkInterceptorRef.current;
    }
  };

  // Helper to detect binary content
  const isBinaryContent = (text: string): boolean => {
    if (text.length === 0) return false;
    
    // Check for null bytes (common in binary)
    if (text.includes('\0')) return true;
    
    // Check for high ratio of non-printable characters
    let nonPrintableCount = 0;
    for (let i = 0; i < Math.min(text.length, 500); i++) {
      const charCode = text.charCodeAt(i);
      if (charCode < 32 && charCode !== 9 && charCode !== 10 && charCode !== 13) {
        nonPrintableCount++;
      }
    }
    
    const ratio = nonPrintableCount / Math.min(text.length, 500);
    return ratio > 0.3; // More than 30% non-printable = likely binary
  };

  // Auto-debug when issues occur
  useEffect(() => {
    if (autoDebug && (debugSteps.some(s => s.status === 'error') || debugAnalyses.some(a => a.severity === 'critical'))) {
      analyzeCurrentState();
    }
  }, [debugSteps, autoDebug]);

  // Smart menu positioning for fixed FAB
  const calculateMenuPosition = useCallback(() => {
    if (!actionsToggleRef.current || !actionsMenuExpanded) return;

    const viewportHeight = window.innerHeight;
    const fabBottomPosition = 24 + 56; // bottom margin + fab height
    const menuHeight = 200; // Estimated menu height
    const minMargin = 16;
    
    // For FAB, check if there's space above for the menu
    const spaceAbove = viewportHeight - fabBottomPosition;
    
    if (spaceAbove >= menuHeight + minMargin) {
      setMenuPosition('top');
    } else {
      // If not enough space above, show below (will extend off screen if needed)
      setMenuPosition('bottom');
    }
  }, [actionsMenuExpanded]);

  useEffect(() => {
    calculateMenuPosition();
    
    const resizeObserver = new ResizeObserver(calculateMenuPosition);
    const mutationObserver = new MutationObserver(calculateMenuPosition);
    
    if (actionsToggleRef.current) {
      resizeObserver.observe(actionsToggleRef.current);
      mutationObserver.observe(document.body, { 
        childList: true, 
        subtree: true, 
        attributes: true 
      });
    }
    
    window.addEventListener('scroll', calculateMenuPosition, { passive: true });
    window.addEventListener('resize', calculateMenuPosition, { passive: true });
    
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('scroll', calculateMenuPosition);
      window.removeEventListener('resize', calculateMenuPosition);
    };
  }, [calculateMenuPosition]);

  // Close menu on outside click (industry standard)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuExpanded && 
          actionsToggleRef.current && 
          actionsDropdownRef.current &&
          !actionsToggleRef.current.contains(event.target as Node) &&
          !actionsDropdownRef.current.contains(event.target as Node)) {
        setActionsMenuExpanded(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && actionsMenuExpanded) {
        setActionsMenuExpanded(false);
      }
      // Ctrl/Cmd + Shift + D to toggle debug panel
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        setDebugPanelVisible(prev => !prev);
      }
    };

    if (actionsMenuExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [actionsMenuExpanded]);

  // Zen processing pipeline
  const parseCodeStep = async (): Promise<any> => {
    updateZenMeditation('generating', zenMessages.parse.emoji, zenMessages.parse.message, 10);
    startDebugStep('parse', { 
      input: {
        code: code,
        knownLanguage,
        targetLanguage,
        codePreview: code.substring(0, 200) + (code.length > 200 ? '...' : '')
      }
    });
    
    try {
      const config: ParserConfig = {
        preferredMethod: 'tree-sitter',
        enableFallback: true, // Enable fallback for first-time issues
        timeoutMs: 10000
      };
      
      // Try parsing with retry logic
      let result;
      let attempts = 0;
      const maxAttempts = 2;
      
      while (attempts < maxAttempts) {
        attempts++;
        console.log(`🔄 Parse attempt ${attempts}/${maxAttempts}...`);
        
        try {
          result = await parseCode({ 
            code, 
            knownLanguage, 
            targetLanguage,
            lenses: {} 
          }, config);
          
          if (result.success) {
            console.log(`✅ Parse successful on attempt ${attempts}`);
            break;
          }
        } catch (parseError) {
          console.warn(`⚠️ Parse attempt ${attempts} failed:`, parseError);
          if (attempts === maxAttempts) throw parseError;
          await new Promise(resolve => setTimeout(resolve, 500)); // Wait before retry
        }
      }
      
      if (result.success && result.annotatedCode) {
        setParseResult(result);
        updateZenMeditation('generating', zenMessages.parse.emoji, zenMessages.parse.message, 20);
        
        completeDebugStep('parse', {
          success: true,
          method: result.method,
          lineCount: result.annotatedCode.lines.length
        }, {
          performance: { method: result.method },
          data: { lineCount: result.annotatedCode.lines.length, codeLength: code.length },
          quality: { success: true }
        });
        
        return result; // Return the actual parse result
      } else {
        throw new Error(result.error || 'Code structure unclear');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      errorDebugStep('parse', errorMsg);
      updateZenMeditation('input', '⚠️', 'Unable to understand code. Please try a different example.', 0);
      return false;
    }
  };

  const enumerateAnchorsStep = async (currentParseResult?: any): Promise<boolean> => {
    updateZenMeditation('generating', zenMessages.enumerate.emoji, zenMessages.enumerate.message, 30);
    
    try {
      console.log('🔥 ENUMERATE DEBUG: Starting enumeration step...');
      
      // Use the provided parse result or fall back to state
      const workingParseResult = currentParseResult || parseResult;
      
      startDebugStep('enumerate', { 
        input: {
          astLines: workingParseResult?.annotatedCode?.lines?.length,
          targetLanguage,
          parseMethod: workingParseResult?.method,
          parsedCode: workingParseResult?.annotatedCode?.lines?.slice(0, 5).map((l: any) => l.content).join('\n')
        }
      });
      
      console.log('📊 ENUMERATE DEBUG: Initial state check:', {
        currentParseResultProvided: !!currentParseResult,
        parseResultFromState: !!parseResult,
        workingParseResult: !!workingParseResult,
        annotatedCode: !!workingParseResult?.annotatedCode,
        linesCount: workingParseResult?.annotatedCode?.lines?.length || 0,
        codeLength: code.length,
        targetLanguage,
        timestamp: new Date().toISOString()
      });

      if (!workingParseResult?.annotatedCode) {
        console.log('🚨 ENUMERATE DEBUG: Parse result validation failed!', {
          currentParseResultProvided: !!currentParseResult,
          parseResultFromState: !!parseResult,
          workingParseResultExists: !!workingParseResult,
          annotatedCodeExists: !!workingParseResult?.annotatedCode,
          workingParseResultKeys: workingParseResult ? Object.keys(workingParseResult) : [],
        });
        throw new Error('No parse result available');
      }

      console.log('🧱 ENUMERATE DEBUG: Parse result structure:', {
        linesCount: workingParseResult.annotatedCode.lines.length,
        firstLineStructure: workingParseResult.annotatedCode.lines[0] ? {
          hasTokens: !!workingParseResult.annotatedCode.lines[0].tokens,
          tokensCount: workingParseResult.annotatedCode.lines[0].tokens?.length || 0,
          lineNumber: workingParseResult.annotatedCode.lines[0].lineNumber,
          firstTokenSample: workingParseResult.annotatedCode.lines[0].tokens?.[0]
        } : 'NO_FIRST_LINE'
      });
      
      // Convert to AST lines
      let globalByteOffset = 0;
      const astLines: ASTLine[] = workingParseResult.annotatedCode.lines.map((line: any) => {
        let lineByteOffset = globalByteOffset;
        const tokens = line.tokens.map((token: any) => {
          const startByte = lineByteOffset;
          const endByte = lineByteOffset + token.text.length;
          lineByteOffset = endByte;
          
          return {
            text: token.text,
            type: token.className || 'unknown',
            className: token.className,
            startByte,
            endByte,
            startPosition: { row: line.lineNumber - 1, column: startByte - globalByteOffset },
            endPosition: { row: line.lineNumber - 1, column: endByte - globalByteOffset },
            nodeId: `node_${line.lineNumber}_${token.text}`
          };
        });
        
        globalByteOffset = lineByteOffset + 1;
        
        return {
          lineNumber: line.lineNumber,
          tokens,
          fullText: line.tokens.map((t: any) => t.text).join('')
        };
      });
      
      console.log('🔍 ENUMERATE DEBUG: AST conversion completed:', {
        originalLinesCount: workingParseResult.annotatedCode.lines.length,
        astLinesCount: astLines.length,
        totalTokensCount: astLines.reduce((sum, line) => sum + line.tokens.length, 0),
        firstAstLine: astLines[0] ? {
          lineNumber: astLines[0].lineNumber,
          tokensCount: astLines[0].tokens.length,
          fullText: astLines[0].fullText,
          firstToken: astLines[0].tokens[0]
        } : 'NO_AST_LINES'
      });
      
      console.log('🔍 Enumerating anchors...');
      console.log('📝 AST Lines count:', astLines.length);
      console.log('🎯 Target language:', targetLanguage.toLowerCase());
      console.log('📋 First AST Line sample:', astLines[0]);
      console.log('📄 Code sample:', code.substring(0, 100));

      console.log('⚙️ ENUMERATE DEBUG: About to call enumerateLensAnchors with options:', {
        astLinesCount: astLines.length,
        codeLength: code.length,
        targetLanguage: targetLanguage.toLowerCase(),
        options: {
          maxAnchors: 50,
          minConfidence: 0.2
        },
        astLinesValid: Array.isArray(astLines),
        astLinesFirstSample: astLines[0] ? {
          lineNumber: astLines[0].lineNumber,
          tokensCount: astLines[0].tokens?.length,
          tokensValid: Array.isArray(astLines[0].tokens)
        } : 'NO_FIRST_LINE'
      });
      
      console.log('🚀 ENUMERATE DEBUG: Making call to enumerateLensAnchors now...');
      
      const enumerated = enumerateLensAnchors(astLines, code, targetLanguage.toLowerCase(), {
        maxAnchors: 50,
        minConfidence: 0.2
      });
      
      console.log('🎯 ENUMERATE DEBUG: enumerateLensAnchors call completed, returned:', {
        result: enumerated,
        resultType: typeof enumerated,
        isArray: Array.isArray(enumerated),
        resultLength: enumerated?.length
      });
      
      console.log('📍 ENUMERATE DEBUG: Enumeration completed:', {
        enumeratedCount: enumerated.length,
        enumeratedPreview: enumerated.slice(0, 3).map(a => ({
          tempId: a.tempId,
          text: a.text,
          nodeType: a.nodeType,
          confidence: a.confidence,
          features: a.features
        }))
      });
      
      console.log('📍 Enumerated anchors:', enumerated.length);
      console.log('🔍 Enumerated anchors preview:', enumerated.slice(0, 3));
      
      // If enumeration failed, let's debug why
      if (enumerated.length === 0) {
        console.log('🚨 ENUMERATE DEBUG: ENUMERATION FAILED - DETAILED ANALYSIS:');
        console.log('🔍 AST structure deep check:', {
          astLinesLength: astLines.length,
          allLinesHaveTokens: astLines.every(line => line.tokens && line.tokens.length > 0),
          tokenDistribution: astLines.map((line, idx) => ({
            lineIdx: idx,
            lineNumber: line.lineNumber,
            tokensCount: line.tokens?.length || 0,
            tokensPreview: line.tokens?.slice(0, 3).map(t => t.text)
          })).slice(0, 5),
          codeLength: code.length,
          targetLang: targetLanguage.toLowerCase(),
          codeFirstLines: code.split('\n').slice(0, 3)
        });

        // Check if all tokens are filtered out
        let totalTokens = 0;
        let processedTokens = 0;
        let filteredByConfidence = 0;
        
        astLines.forEach(line => {
          line.tokens.forEach(() => {
            totalTokens++;
            
            // Simulate the filtering logic from enumerateLensAnchors
            const confidence = 0.5; // Would calculate confidence here
            
            if (confidence >= 0.2) {
              processedTokens++;
            } else {
              filteredByConfidence++;
            }
          });
        });

        console.log('🔍 ENUMERATE DEBUG: Token filtering analysis:', {
          totalTokens,
          processedTokens, 
          filteredByConfidence,
          filteringRatio: totalTokens > 0 ? filteredByConfidence / totalTokens : 0
        });
      }
      
      const deduplicated = deduplicateLensAnchors(enumerated);
      console.log('✨ ENUMERATE DEBUG: Deduplication completed:', {
        beforeCount: enumerated.length,
        afterCount: deduplicated.length,
        duplicatesRemoved: enumerated.length - deduplicated.length,
        deduplicatedPreview: deduplicated.slice(0, 3).map(a => ({
          tempId: a.tempId,
          text: a.text,
          confidence: a.confidence
        }))
      });
      console.log('✨ Deduplicated anchors:', deduplicated.length);
      
      setAnchors(deduplicated);
      
      // Continue even with minimal anchors - match working version behavior
      console.log('🎯 ENUMERATE DEBUG: Final result:', {
        finalAnchorCount: deduplicated.length,
        proceeding: true,
        timestamp: new Date().toISOString()
      });
      console.log('🎯 Proceeding with anchors:', deduplicated.length, 'found');
      
      completeDebugStep('enumerate', {
        astLinesCount: astLines.length,
        enumeratedCount: enumerated.length,
        deduplicatedCount: deduplicated.length,
        anchorsPreview: deduplicated.slice(0, 3)
      }, {
        performance: { enumerationTime: performance.now() },
        data: { 
          astLinesCount: astLines.length, 
          finalAnchorCount: deduplicated.length,
          duplicatesRemoved: enumerated.length - deduplicated.length
        },
        quality: { 
          enumerationSuccess: enumerated.length > 0,
          deduplicationRatio: enumerated.length > 0 ? deduplicated.length / enumerated.length : 0
        }
      });
      
      updateZenMeditation('generating', zenMessages.enumerate.emoji, zenMessages.enumerate.message, 45);
      return true;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.log('🚨 ENUMERATE DEBUG: Exception caught!', {
        errorMessage: errorMsg,
        errorStack: error instanceof Error ? error.stack : 'No stack',
        errorType: typeof error,
        timestamp: new Date().toISOString()
      });
      
      errorDebugStep('enumerate', errorMsg);
      updateZenMeditation('input', '⚠️', 'Unable to find learning opportunities in this code.', 0);
      return false;
    }
  };

  // NEW: LLM Semantic Picks (Step 2) - LLM chooses what matters first
  const llmSemanticPicksStep = async (currentParseResult: any): Promise<boolean> => {
    updateZenMeditation('generating', '🧠', 'AI analyzing code for learning insights...', 30);
    
    const requestId = llmLogger.generateRequestId();
    const estimatedTokenSavings = calculateTokenSavings(code.length, 0); // No anchors enumerated yet!
    
    startDebugStep('semantic', { 
      input: {
        codeLength: code.length,
        knownLanguage,
        targetLanguage,
        codePreview: code.substring(0, 200) + (code.length > 200 ? '...' : '')
      },
      requestId,
      estimatedTokenSavings
    });
    
    try {
      console.log('🧠 LLM analyzing code semantically without enumeration...');
      
      // Log request
      llmLogger.logRequest({
        requestId,
        component: 'ZenPlaygroundOptimized',
        known: knownLanguage,
        target: targetLanguage,
        endpoint: '/api/lens-anchors/suggest-from-code',
        anchorCount: 0, // No pre-enumeration!
        payload: {
          code,
          known: knownLanguage,
          target: targetLanguage,
          max: 8,
          optimization: 'semantic-first' // New flag
        }
      });
      
      const startTime = Date.now();
      
      // Get preferred provider from Zen settings
      const preferredProvider = getPreferredProvider(providers);
      
      const response = await selectAnchorsFromLLM({
        code,
        known: knownLanguage,
        target: targetLanguage,
        max: 8,
        provider: preferredProvider || undefined
      });
      const endTime = Date.now();
      
      // Debug: Log the actual response structure
      console.log('🔍 LLM Response Debug:', {
        metadata: response.metadata,
        hasPrompt: !!response.metadata?.prompt,
        promptLength: response.metadata?.prompt?.length,
        tokensUsed: response.metadata?.tokensUsed
      });
      
      // Capture AI Observability Data
      const aiObservability: AIObservabilityData = {
        apiCall: {
          endpoint: '/api/lens-anchors/suggest-from-code',
          method: 'POST',
          timestamp: new Date(startTime).toISOString(),
          requestId: `semantic_${startTime}`
        },
        prompt: {
          content: response.metadata.prompt || 'Prompt not captured by backend',
          tokenCount: response.metadata.tokensUsed?.input,
          parameters: {
            known: knownLanguage,
            target: targetLanguage,
            max: 8,
            codeLength: code.length
          }
        },
        response: {
          content: response.selections,
          tokenCount: response.metadata.tokensUsed?.output,
          responseTimeMs: endTime - startTime,
          statusCode: 200
        },
        metadata: {
          model: response.metadata.model || 'claude-sonnet-4-20250514',
          provider: 'anthropic',
          cost: response.metadata.tokensUsed ? (response.metadata.tokensUsed.total * 0.003 / 1000) : undefined, // Anthropic pricing
          cacheHit: false
        }
      };
      
      // Store AI observability data (accumulate, don't overwrite)
      setAiObservabilityData(prev => {
        const newMap = new Map(prev);
        newMap.set(`semantic_${startTime}`, aiObservability);
        console.log('🔍 AI Observability - Stored semantic data:', {
          key: `semantic_${startTime}`,
          totalEntries: newMap.size,
          keys: Array.from(newMap.keys()),
          previousSize: prev.size,
          hasPrompt: !!aiObservability.prompt.content,
          promptPreview: aiObservability.prompt.content.substring(0, 100)
        });
        return newMap;
      });
      
      // Store LLM selections for next step
      (window as any).__llmSelections = response.selections;
      console.log('🧠 LLM picked semantic anchors:', response.selections.length);
      
      completeDebugStep('semantic', {
        selectionsCount: response.selections.length,
        tokensSaved: response.metadata.tokensSaved,
        responseTime: response.metadata.responseTime,
        llmSelectionsFull: response.selections, // Include full selections data
        aiObservability // Include AI observability data
      }, {
        performance: { responseTime: response.metadata.responseTime },
        data: { 
          inputCodeLength: code.length,
          outputSelections: response.selections.length,
          semanticCategories: response.selections.map((s: any) => s.diffType).filter(Boolean)
        },
        quality: {
          avgNovelty: response.selections.reduce((sum: any, s: any) => sum + s.novelty, 0) / response.selections.length,
          hasReasons: response.selections.every((s: any) => s.reason && s.reason.length > 10)
        }
      });
      
      updateZenMeditation('generating', '🧠', 'AI found valuable learning insights', 50);
      return true;
      
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      errorDebugStep('semantic', errorMsg);
      updateZenMeditation('input', '⚠️', 'Unable to analyze code semantically', 0);
      return false;
    }
  };

  // LEGACY: Old LLM select step (keeping for reference)  
  const llmSelectStep = async (): Promise<boolean> => {
    updateZenMeditation('generating', zenMessages.select.emoji, zenMessages.select.message, 60);
    
    const requestId = llmLogger.generateRequestId();
    const estimatedTokenSavings = calculateTokenSavings(code.length, anchors.length);
    
    startDebugStep('select', { 
      input: {
        anchorsCount: anchors.length,
        codeLength: code.length,
        knownLanguage,
        targetLanguage,
        anchorsPreview: anchors.slice(0, 3).map(a => ({ 
          text: a.text.substring(0, 50), 
          type: a.anchorType 
        }))
      },
      requestId,
      estimatedTokenSavings
    });
    
    try {
      console.log('📡 Making API call to selectAnchorsFromLLM...');
      console.log('📊 Request params:', { knownLanguage, targetLanguage, codeLength: code.length, anchorsCount: anchors.length });
      
      // Log optimized request
      llmLogger.logRequest({
        requestId,
        component: 'ZenPlaygroundOptimized',
        known: knownLanguage,
        target: targetLanguage,
        endpoint: '/api/lens-anchors/suggest-from-code',
        anchorCount: anchors.length,
        payload: {
          code,
          known: knownLanguage,
          target: targetLanguage,
          max: 8,
          optimization: 'code-first',
          estimatedTokenSavings
        }
      });
      
      // Add timeout to the API call
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('API call timeout')), 30000)
      );
      
      const apiPromise = selectAnchorsFromLLM({
        code,
        known: knownLanguage,
        target: targetLanguage,
        max: 8,
        provider: preferredProvider || undefined
      });
      
      console.log('⏰ Starting API call with 30s timeout...');
      const result = await Promise.race([apiPromise, timeoutPromise]) as any;
      console.log('🎉 API call successful!', result);
      
      // Log successful response
      llmLogger.logResponse({
        requestId,
        component: 'ZenPlaygroundOptimized',
        known: knownLanguage,
        target: targetLanguage,
        responseTimeMs: performance.now(),
        status: 'success',
        picks: result.selections.length,
        payload: {
          selections: result.selections,
          tokensSaved: result.metadata.tokensSaved,
          optimization: 'zen-optimized'
        }
      });
      
      setOptimizationStats({
        tokensSaved: result.metadata.tokensSaved,
        responseTime: performance.now(),
        matchAccuracy: 0,
        originalTokens: result.metadata.originalTokens || 612,
        optimizedTokens: result.metadata.optimizedTokens || 100
      });
      
      completeDebugStep('select', {
        selectionsCount: result.selections.length,
        tokensSaved: result.metadata.tokensSaved,
        originalTokens: result.metadata.originalTokens,
        optimizedTokens: result.metadata.optimizedTokens,
        selections: result.selections
      }, {
        performance: { 
          responseTime: performance.now(),
          tokenSavingsRatio: result.metadata.tokensSaved / (result.metadata.originalTokens || 612)
        },
        data: {
          selectionsGenerated: result.selections.length,
          targetSelections: 8,
          fulfillmentRate: result.selections.length / 8
        },
        quality: {
          selectionSuccess: result.selections.length > 0,
          optimizationAchieved: result.metadata.tokensSaved > 0
        }
      });
      
      updateZenMeditation('generating', zenMessages.select.emoji, zenMessages.select.message, 75);
      
      // Store for matching step
      (window as any).__llmSelections = result.selections;
      console.log('💾 Stored selections for matching:', result.selections.length);
      
      return true;
    } catch (error) {
      console.error('💥 API call failed:', error);
      
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      errorDebugStep('select', errorMsg);
      
      llmLogger.logError({
        requestId,
        component: 'ZenPlaygroundOptimized',
        known: knownLanguage,
        target: targetLanguage,
        error
      });
      
      updateZenMeditation('input', '⚠️', 'Unable to discover meaningful insights. Please try different code.', 0);
      return false;
    }
  };

  // NEW: Validate LLM Picks with AST (Step 3) - Only enumerate what's needed
  const validateLLMWithASTStep = async (currentParseResult: any): Promise<boolean> => {
    updateZenMeditation('generating', '🔍', 'Validating insights with code structure...', 70);
    
    console.log('🔍 === VALIDATION DEBUG START ===');
    console.log('🔍 Window object keys:', Object.keys(window).filter(k => k.includes('llm')));
    
    const llmSelections = (window as any).__llmSelections;
    console.log('🔍 Raw LLM selections from window:', {
      exists: !!llmSelections,
      type: typeof llmSelections,
      isArray: Array.isArray(llmSelections),
      length: llmSelections?.length,
      keys: llmSelections ? Object.keys(llmSelections) : 'N/A'
    });
    
    console.log('🔍 Current parse result structure:', {
      exists: !!currentParseResult,
      type: typeof currentParseResult,
      keys: currentParseResult ? Object.keys(currentParseResult) : 'N/A',
      hasAnnotatedCode: !!currentParseResult?.annotatedCode,
      annotatedCodeType: typeof currentParseResult?.annotatedCode,
      annotatedCodeKeys: currentParseResult?.annotatedCode ? Object.keys(currentParseResult.annotatedCode) : 'N/A'
    });
    
    console.log('🔍 Code variable:', {
      codeLength: code?.length,
      codePreview: code?.substring(0, 100),
      codeType: typeof code
    });
    
    startDebugStep('resolve', { 
      input: {
        llmSelectionsCount: llmSelections?.length || 0,
        llmSelectionsPreview: llmSelections?.slice(0, 3).map((s: any) => ({
          text: s.text?.substring(0, 50),
          novelty: s.novelty,
          reason: s.reason?.substring(0, 100)
        })),
        llmSelectionsFull: llmSelections, // Include full data for debug panel
        parseResultExists: !!currentParseResult,
        parseResultKeys: currentParseResult ? Object.keys(currentParseResult) : [],
        codeExists: !!code,
        codeLength: code?.length
      }
    });
    
    try {
      console.log('🔍 Validation checks:');
      console.log('🔍 - llmSelections exists:', !!llmSelections);
      console.log('🔍 - llmSelections is array:', Array.isArray(llmSelections));
      console.log('🔍 - llmSelections length:', llmSelections?.length);
      console.log('🔍 - currentParseResult exists:', !!currentParseResult);
      console.log('🔍 - annotatedCode exists:', !!currentParseResult?.annotatedCode);
      
      if (!llmSelections || llmSelections.length === 0) {
        console.error('❌ VALIDATION FAILED: No LLM selections to validate');
        throw new Error('No LLM selections to validate');
      }
      
      if (!currentParseResult || !currentParseResult.annotatedCode) {
        console.error('❌ VALIDATION FAILED: No parse result to validate against');
        console.error('❌ Parse result details:', {
          parseResultExists: !!currentParseResult,
          parseResultType: typeof currentParseResult,
          parseResultKeys: currentParseResult ? Object.keys(currentParseResult) : 'N/A',
          annotatedCodeExists: !!currentParseResult?.annotatedCode
        });
        throw new Error('No parse result to validate against');
      }
      
      console.log('🔍 Validating LLM picks against AST...');
      console.log('🔍 Parse result structure:', {
        hasAnnotatedCode: !!currentParseResult.annotatedCode,
        hasLines: !!currentParseResult.annotatedCode?.lines,
        linesCount: currentParseResult.annotatedCode?.lines?.length
      });
      
      console.log('🔍 LLM selections to validate:', {
        count: llmSelections.length,
        sample: llmSelections.slice(0, 2).map((s: any) => ({ 
          text: s.text, 
          novelty: s.novelty 
        }))
      });
      
      // Debug: Log full text of first selection
      if (llmSelections.length > 0) {
        console.log('🎯 First LLM selection full text:', {
          fullText: llmSelections[0].text,
          textLength: llmSelections[0].text.length,
          startsWithFn: llmSelections[0].text.startsWith('fn '),
          includesBrace: llmSelections[0].text.includes('{')
        });
      }
      
      // SMART ENUMERATION: Only enumerate tokens that might match LLM picks
      const targetTexts = llmSelections.map((s: any) => s.text).filter(Boolean);
      console.log('🎯 Target texts extracted:', targetTexts);
      
      if (targetTexts.length === 0) {
        throw new Error('No valid target texts from LLM selections');
      }
      
      // REVOLUTIONARY: Use Tree-sitter Query Engine for semantic AST understanding
      // Since we're in simulation mode, create a compatible tree structure
      const simulatedTree = {
        rootNode: {
          type: 'source_file',
          startPosition: { row: 0, column: 0 },
          endPosition: { row: code.split('\n').length, column: 0 },
          startIndex: 0,
          endIndex: code.length,
          text: code,
          children: [],
          walk: () => ({
            currentNode: { type: 'placeholder', text: '', children: [] },
            gotoFirstChild: () => false,
            gotoNextSibling: () => false,
            gotoParent: () => false
          })
        }
      };
      console.log('🔍 Created TreeSitterQueryEngine with simulated tree');
      const queryEngine = new TreeSitterQueryEngine(simulatedTree as any);
      
      console.log('🔍 Starting semantic matching for', llmSelections.length, 'selections');
      const directSelections = llmSelections.map((selection: any, index: number) => {
        try {
          console.log(`🔍 === Processing LLM selection ${index + 1}/${llmSelections.length} ===`);
          console.log(`🔍 Selection details:`, {
            text: selection.text,
            textLength: selection.text.length,
            reason: selection.reason,
            diffType: selection.diffType,
            novelty: selection.novelty
          });
          
          let semanticMatches: SemanticMatch[] = [];
          try {
            // Use semantic query engine to find matches
            console.log(`🔍 Calling queryEngine.findSemanticMatches...`);
            semanticMatches = queryEngine.findSemanticMatches(selection, code);
            console.log(`🔍 Semantic matching result:`, {
              matchCount: semanticMatches.length,
              matches: semanticMatches.map(m => ({
                context: m.context,
                confidence: m.confidence,
                textCoverage: m.textCoverage,
                semanticRelevance: m.semanticRelevance,
                nodeText: m.node.text.substring(0, 30)
              }))
            });
          } catch (error) {
            console.warn(`⚠️ Semantic matching failed for "${selection.text}":`, error);
            semanticMatches = [];
          }
        
        let bestMatch: SemanticMatch | null = null;
        let validationMethod = 'semantic-ast';
        
        if (semanticMatches.length > 0) {
          // Choose the best semantic match
          bestMatch = semanticMatches[0]; // Already sorted by semantic relevance
          console.log(`✅ Semantic match found for "${selection.text.substring(0, 30)}...":`, {
            context: bestMatch.context,
            confidence: bestMatch.confidence,
            textCoverage: bestMatch.textCoverage,
            semanticRelevance: bestMatch.semanticRelevance,
            completeExpression: bestMatch.completeExpression,
            nodeText: bestMatch.node.text.substring(0, 50)
          });
        } else {
          console.warn(`❌ No semantic matches found for "${selection.text}"`);
          // Fall back to enhanced text positioning
          const textBasedPosition = findTextPosition(code, selection.text);
          if (textBasedPosition) {
            validationMethod = 'text-based';
            bestMatch = {
              node: {
                text: selection.text,
                startPosition: textBasedPosition.startPoint,
                endPosition: textBasedPosition.endPoint,
                type: 'text-based'
              } as any,
              context: 'unknown',
              confidence: 0.7,
              completeExpression: false,
              textCoverage: 100,
              semanticRelevance: 0.5
            };
          } else {
            validationMethod = 'synthetic';
            bestMatch = {
              node: {
                text: selection.text,
                startPosition: { row: 0, column: 0 },
                endPosition: { row: 0, column: selection.text.length },
                type: 'synthetic'
              } as any,
              context: 'unknown',
              confidence: 0.3,
              completeExpression: false,
              textCoverage: 100,
              semanticRelevance: 0.1
            };
          }
        }
        
        // Create enhanced selection with semantic understanding
        const enhancedSelection = {
          tempId: `llm_${Date.now()}_${index}`,
          text: selection.text,
          novelty: selection.novelty,
          diffType: Array.isArray(selection.diffType) ? selection.diffType : [selection.diffType || 'semantic'],
          why: selection.reason,
          granularity: bestMatch ? (bestMatch.completeExpression ? 'expression' : 'token') : 'span',
          group: 'llm_semantic',
          priority: 1,
          // Enhanced span information
          span: {
            startPoint: bestMatch!.node.startPosition,
            endPoint: bestMatch!.node.endPosition,
            startByte: bestMatch!.node.startIndex || 0,
            endByte: bestMatch!.node.endIndex || bestMatch!.node.text.length
          },
          nodeType: bestMatch!.node.type,
          features: [bestMatch!.context, validationMethod],
          // Enhanced validation metrics
          astValidated: bestMatch!.semanticRelevance > 0.5,
          confidence: bestMatch!.confidence,
          validationMethod,
          semanticContext: bestMatch!.context,
          semanticRelevance: bestMatch!.semanticRelevance,
          textCoverage: bestMatch!.textCoverage,
          completeExpression: bestMatch!.completeExpression,
          // Legacy compatibility
          spanMismatch: bestMatch!.textCoverage < 80
        };

          return enhancedSelection;
        } catch (selectionError) {
          console.error(`❌ Error processing selection ${index}:`, selectionError);
          console.error(`❌ Selection that caused error:`, {
            text: selection.text,
            reason: selection.reason,
            diffType: selection.diffType
          });
          
          // Create fallback selection
          return {
            tempId: `fallback_${Date.now()}_${index}`,
            text: selection.text,
            novelty: selection.novelty,
            diffType: ['fallback'],
            why: selection.reason,
            granularity: 'token',
            group: 'fallback',
            priority: 1,
            span: {
              startPoint: { row: 0, column: 0 },
              endPoint: { row: 0, column: selection.text.length },
              startByte: 0,
              endByte: selection.text.length
            },
            nodeType: 'error',
            features: ['error', 'fallback'],
            astValidated: false,
            confidence: 0.1,
            validationMethod: 'error_fallback',
            semanticContext: 'unknown',
            semanticRelevance: 0.1,
            textCoverage: 0,
            completeExpression: false,
            spanMismatch: true
          };
        }
      });
      
      console.log('🔍 Final direct selections count:', directSelections.length);
      console.log('🔍 Direct selections sample:', directSelections.slice(0, 2).map(s => ({
        text: s.text?.substring(0, 30),
        astValidated: s.astValidated,
        confidence: s.confidence,
        validationMethod: s.validationMethod
      })));
      
      setSelectedAnchors(directSelections);
      (window as any).__directSelections = directSelections;
      
      // IMPORTANT: Also populate anchors for highlighting
      // Convert validated selections to anchor format for UI highlighting
      const anchorsForHighlighting = directSelections.map((selection: any, idx: number) => {
        console.log(`🎨 Creating anchor for highlighting ${idx}:`, {
          selectionText: selection.text,
          hasSpan: !!selection.span,
          spanStartPoint: selection.span?.startPoint,
          spanEndPoint: selection.span?.endPoint,
          astValidated: selection.astValidated
        });
        
        // If we have a proper span from AST validation, use it
        if (selection.span && selection.span.startPoint) {
          // IMPORTANT: Make sure we're not using a wrong span from AST
          // If the span seems too small for the text, recalculate
          if (selection.span.endPoint) {
            const spanLength = (selection.span.endPoint.column - selection.span.startPoint.column);
            if (spanLength < selection.text.length / 2) {
              console.warn(`⚠️ Span too small for text! Span covers ${spanLength} chars but text is ${selection.text.length} chars. Recalculating...`);
              const recalculatedPosition = findTextPosition(code, selection.text);
              if (recalculatedPosition) {
                selection.span = recalculatedPosition;
                console.log('✅ Recalculated position:', recalculatedPosition);
              }
            }
          }
          
          return {
            tempId: selection.tempId,
            text: selection.text,
            nodeType: selection.nodeType || 'semantic',
            anchorType: 'llm-selection',
            confidence: selection.confidence || selection.novelty,
            features: selection.diffType || ['semantic'],
            span: selection.span,
            granularity: selection.granularity || 'span'
          };
        }
        
        // Otherwise, use accurate position finding (Monaco Editor style)
        const position = findTextPosition(code, selection.text);
        
        // Debug: Log position calculation
        console.log('🎯 Position calculation for selection:', {
          text: selection.text.substring(0, 50) + (selection.text.length > 50 ? '...' : ''),
          textLength: selection.text.length,
          position,
          foundInCode: !!position,
          selectionFullText: selection.text
        });
        
        // If position finding failed and this looks like it might be truncated, try to expand it
        if (!position && selection.text.length < 30) {
          console.log('⚠️ Short text failed to find position, might be truncated:', selection.text);
        }
        
        return {
          tempId: selection.tempId,
          text: selection.text,
          nodeType: selection.nodeType || 'semantic',
          anchorType: 'llm-selection',
          confidence: selection.confidence || selection.novelty,
          features: selection.diffType || ['semantic'],
          span: position || {
            // Fallback if text not found exactly
            startPoint: { row: 0, column: 0 },
            endPoint: { row: 0, column: selection.text.length }
          },
          granularity: selection.granularity || 'span'
        };
      });
      
      // Set anchors for UI highlighting
      setAnchors(anchorsForHighlighting);
      // IMPORTANT: Store in window for fallback access
      (window as any).__anchorsForHighlighting = anchorsForHighlighting;
      console.log('🎨 Set anchors for highlighting:', anchorsForHighlighting.length);
      console.log('🔍 TempId tracking - Anchors stored:', anchorsForHighlighting.map(a => ({
        tempId: a.tempId,
        text: a.text.substring(0, 30) + '...',
        hasSpan: !!a.span
      })));
      
      // Prepare detailed AST debug data
      const astDebugData = {
        llmSelections: llmSelections.map((s: any) => ({
          text: s.text,
          textLength: s.text.length,
          novelty: s.novelty,
          reason: s.reason
        })),
        smartAnchors: anchorsForHighlighting.map((a: any) => ({
          text: a.text,
          span: a.span,
          nodeType: a.nodeType,
          granularity: a.granularity
        })),
        semanticMatchingDetails: directSelections.map((sel: any, idx: number) => {
          const llmSel = llmSelections[idx];
          
          return {
            index: idx,
            llmText: llmSel.text,
            llmTextLength: llmSel.text.length,
            llmReason: llmSel.reason,
            llmDiffType: llmSel.diffType,
            
            // Semantic AST Intelligence Results
            semanticContext: sel.semanticContext || 'unknown',
            validationMethod: sel.validationMethod,
            semanticRelevance: sel.semanticRelevance || 0,
            textCoverage: sel.textCoverage || 0,
            completeExpression: sel.completeExpression || false,
            confidence: sel.confidence || 0,
            
            // Node Information
            nodeType: sel.nodeType || 'unknown',
            nodeText: sel.span ? 
              code.substring(
                code.split('\n').slice(0, sel.span.startPoint.row).join('\n').length + sel.span.startPoint.column,
                code.split('\n').slice(0, sel.span.endPoint.row).join('\n').length + sel.span.endPoint.column
              ).substring(0, 50) : 'N/A',
            
            // Span Information
            finalSpan: sel.span,
            spanValid: !!(sel.span && sel.span.startPoint && sel.span.endPoint),
            astValidated: sel.astValidated,
            
            // Performance Metrics
            textCoveragePercentage: sel.textCoverage?.toFixed(1) + '%' || 'N/A',
            semanticRelevancePercentage: (sel.semanticRelevance * 100)?.toFixed(1) + '%' || 'N/A',
            
            // Issue Detection
            hasIssues: sel.semanticRelevance < 0.7 || sel.textCoverage < 80,
            issues: [
              sel.semanticRelevance < 0.7 ? 'Low semantic relevance' : null,
              sel.textCoverage < 80 ? 'Incomplete text coverage' : null,
              !sel.completeExpression ? 'Partial expression' : null,
              sel.validationMethod === 'synthetic' ? 'Fallback to synthetic span' : null
            ].filter(Boolean)
          };
        }),
        anchorsForHighlighting: anchorsForHighlighting.map((a: any) => ({
          tempId: a.tempId,
          text: a.text.substring(0, 50) + (a.text.length > 50 ? '...' : ''),
          span: a.span,
          spanCoverage: a.span ? 
            ((a.span.endPoint?.column || 0) - (a.span.startPoint?.column || 0)) + ' chars' : 'no span'
        }))
      };
      
      // Enhanced validation summary with semantic intelligence metrics
      const validationSummary = {
        totalLLMSelections: llmSelections.length,
        
        // Semantic AST Intelligence Metrics
        semanticASTValidated: directSelections.filter(s => s.validationMethod === 'semantic-ast').length,
        textBasedFallback: directSelections.filter(s => s.validationMethod === 'text-based').length,
        syntheticFallback: directSelections.filter(s => s.validationMethod === 'synthetic').length,
        
        // Quality Metrics
        highConfidenceMatches: directSelections.filter(s => s.semanticRelevance > 0.8).length,
        completeExpressions: directSelections.filter(s => s.completeExpression).length,
        fullTextCoverage: directSelections.filter(s => s.textCoverage >= 90).length,
        
        // Context Distribution
        semanticContexts: directSelections.reduce((acc, s) => {
          const context = s.semanticContext || 'unknown';
          acc[context] = (acc[context] || 0) + 1;
          return acc;
        }, {} as Record<string, number>),
        
        // Validation Methods Distribution
        validationMethods: directSelections.reduce((acc, s) => {
          acc[s.validationMethod || 'unknown'] = (acc[s.validationMethod || 'unknown'] || 0) + 1;
          return acc;
        }, {} as Record<string, number>),
        
        // Performance Averages
        avgSemanticRelevance: directSelections.reduce((sum, s) => sum + (s.semanticRelevance || 0), 0) / directSelections.length,
        avgTextCoverage: directSelections.reduce((sum, s) => sum + (s.textCoverage || 0), 0) / directSelections.length,
        avgConfidence: directSelections.reduce((sum, s) => sum + (s.confidence || 0), 0) / directSelections.length,
        
        // Issue Detection
        issuesDetected: directSelections.filter(s => 
          (s.semanticRelevance || 0) < 0.7 || 
          (s.textCoverage || 0) < 80 || 
          s.validationMethod === 'synthetic'
        ).length,
        
        // Legacy compatibility
        astValidated: directSelections.filter(s => s.astValidated).length,
        spanMismatches: directSelections.filter(s => s.spanMismatch).length
      };
      
      console.log('🎯 AST Validation Summary:', validationSummary);
      
      completeDebugStep('resolve', {
        validatedCount: directSelections.filter(s => s.astValidated).length,
        syntheticCount: directSelections.filter(s => !s.astValidated).length,
        totalSelections: directSelections.length,
        validationSummary,
        astDebugData // Include the detailed AST debug data
      }, {
        performance: { validationTime: performance.now() },
        data: {
          inputLLMSelections: llmSelections.length,
          outputValidated: directSelections.length,
          astMatchRate: directSelections.filter(s => s.astValidated).length / directSelections.length
        },
        quality: {
          validationSuccess: directSelections.length > 0,
          astMatchRate: directSelections.filter(s => s.astValidated).length / directSelections.length,
          avgConfidence: directSelections.reduce((sum, s) => sum + (s.confidence || 0), 0) / directSelections.length
        }
      });
      
      updateZenMeditation('generating', '🔍', 'Insights validated and ready', 85);
      return true;
      
    } catch (error) {
      console.error('❌ VALIDATION CAUGHT ERROR:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : 'No stack trace',
        errorType: typeof error,
        errorName: error instanceof Error ? error.name : 'Unknown error type'
      });
      
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Validation step failed with error:', errorMsg);
      errorDebugStep('resolve', errorMsg);
      updateZenMeditation('input', '⚠️', 'Unable to validate insights with code structure', 0);
      return false;
    }
  };

  // Helper: Robust text position finder (Monaco Editor style)
  const findTextPosition = (sourceCode: string, searchText: string) => {
    if (!sourceCode || !searchText) return null;
    
    // Normalize line endings for consistent processing
    const normalizedSource = sourceCode.replace(/\r\n/g, '\n');
    const normalizedSearch = searchText.replace(/\r\n/g, '\n').trim();
    
    console.log('🔍 Finding position for:', { 
      searchText: normalizedSearch,
      searchLength: normalizedSearch.length,
      sourceCodePreview: sourceCode.substring(0, 100),
      searchTextRaw: searchText
    });
    
    // Try exact match first
    let startIndex = normalizedSource.indexOf(normalizedSearch);
    let matchMethod = 'exact';
    
    if (startIndex === -1) {
      // Try with different quotation marks (smart quotes, etc.)
      const searchVariants = [
        normalizedSearch,
        normalizedSearch.replace(/"/g, '"').replace(/"/g, '"'),  // Smart quotes to regular
        normalizedSearch.replace(/'/g, "'").replace(/'/g, "'"),  // Smart single quotes
        normalizedSearch.replace(/"/g, "'"),  // Double to single quotes
        normalizedSearch.replace(/'/g, '"')   // Single to double quotes
      ];
      
      for (const variant of searchVariants) {
        startIndex = normalizedSource.indexOf(variant);
        if (startIndex !== -1) {
          matchMethod = 'variant';
          console.log('🎯 Found with variant:', variant);
          break;
        }
      }
    }
    
    if (startIndex === -1) {
      // Special case: if search text looks like a function signature without the opening brace
      // e.g., "fn create_user() -> Box<User>" when code has "fn create_user() -> Box<User> {"
      if (normalizedSearch.startsWith('fn ') && !normalizedSearch.includes('{')) {
        // Try adding the opening brace
        const withBrace = normalizedSearch + ' {';
        const withBraceIndex = normalizedSource.indexOf(withBrace);
        if (withBraceIndex !== -1) {
          startIndex = withBraceIndex;
          matchMethod = 'signature-with-brace';
          console.log('🎯 Found function signature by adding brace');
          // Note: We still use normalizedSearch.length for the end position
        } else {
          // Try without spaces before brace
          const withBraceNoSpace = normalizedSearch + '{';
          const withBraceNoSpaceIndex = normalizedSource.indexOf(withBraceNoSpace);
          if (withBraceNoSpaceIndex !== -1) {
            startIndex = withBraceNoSpaceIndex;
            matchMethod = 'signature-with-brace-no-space';
            console.log('🎯 Found function signature by adding brace (no space)');
          }
        }
      }
    }
    
    if (startIndex === -1) {
      // Enhanced Rust-specific pattern matching
      const rustPatterns = [
        // Rust function signatures - extract the core part
        normalizedSearch.match(/fn\s+\w+\s*\([^)]*\)/)?.[0],
        // Rust return type arrow
        normalizedSearch.match(/->\s*[^{]+/)?.[0],
        // Type expressions with generics
        normalizedSearch.match(/\b\w+<[^>]+>/)?.[0],
        // Function calls like String::from, Box::new  
        normalizedSearch.match(/\b\w+::\w+/)?.[0],
        // Struct field patterns
        normalizedSearch.match(/\w+:\s*\w+/)?.[0],
        // Quoted strings
        normalizedSearch.match(/"[^"]*"/)?.[0],
        // Type identifiers (Box, String, User, etc.)
        normalizedSearch.match(/\b[A-Z]\w*/)?.[0],
        // General word patterns
        normalizedSearch.match(/\b\w+(?:\s+\w+)*\b/)?.[0]
      ].filter(Boolean);
      
      // Also try regex-based fuzzy matching for complex expressions
      if (normalizedSearch.includes('->') || normalizedSearch.includes('::') || normalizedSearch.startsWith('fn ')) {
        // For function signatures, try more flexible matching
        const fnNameMatch = normalizedSearch.match(/fn\s+(\w+)/);
        if (fnNameMatch) {
          const fnName = fnNameMatch[1];
          // Look for the function definition line
          const lines = normalizedSource.split('\n');
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (line.includes(`fn ${fnName}`) && line.includes('->')) {
              // Try to match the full signature in this line
              const signatureMatch = line.match(/fn\s+\w+[^{]*/);
              if (signatureMatch && normalizedSearch.startsWith(signatureMatch[0].trim())) {
                const lineStart = normalizedSource.indexOf(line);
                const signatureStart = lineStart + line.indexOf(signatureMatch[0]);
                startIndex = signatureStart;
                matchMethod = 'rust-function-signature';
                console.log('🦀 Found Rust function signature:', signatureMatch[0]);
                break;
              }
            }
          }
        }
      }
      
      const corePatterns = rustPatterns;
      
      console.log('🔍 Trying core patterns:', corePatterns);
      
      for (const pattern of corePatterns) {
        if (pattern && pattern.length >= 3) {  // Only try meaningful patterns
          const patternIndex = normalizedSource.indexOf(pattern);
          if (patternIndex !== -1) {
            // Now try to find the full text around this pattern
            const lineStart = normalizedSource.lastIndexOf('\n', patternIndex) + 1;
            const lineEnd = normalizedSource.indexOf('\n', patternIndex);
            const line = normalizedSource.substring(lineStart, lineEnd === -1 ? undefined : lineEnd);
            
            console.log('🔍 Found pattern in line:', { pattern, line });
            
            // Look for the full search text in this line
            const fullTextInLine = line.indexOf(normalizedSearch);
            if (fullTextInLine !== -1) {
              startIndex = lineStart + fullTextInLine;
              matchMethod = 'pattern-full';
              console.log('🎯 Found full text in line at:', startIndex);
              break;
            } else {
              // Check if the pattern is part of what we're looking for
              const patternInSearch = normalizedSearch.indexOf(pattern);
              if (patternInSearch !== -1) {
                // Calculate where the search text should start
                const searchStart = patternIndex - patternInSearch;
                if (searchStart >= 0) {
                  // Verify this gives us the right text
                  const candidateText = normalizedSource.substring(searchStart, searchStart + normalizedSearch.length);
                  if (candidateText === normalizedSearch) {
                    startIndex = searchStart;
                    matchMethod = 'pattern-calculated';
                    console.log('🎯 Calculated position from pattern:', startIndex);
                    break;
                  }
                  
                  // For partial matches, try expanding around the pattern
                  // This handles cases like "Box::new(User {" where we found "Box::new" but need more
                  if (normalizedSearch.includes(pattern)) {
                    const expandedSearch = expandAroundPattern(normalizedSource, patternIndex, normalizedSearch, pattern);
                    if (expandedSearch.found) {
                      startIndex = expandedSearch.startIndex;
                      matchMethod = 'pattern-expanded';
                      console.log('🎯 Found by expanding around pattern:', { pattern, expandedText: expandedSearch.text });
                      break;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    
    if (startIndex === -1) {
      console.warn('🚨 Could not find text position for:', normalizedSearch);
      return null;
    }
    
    // Verify we found the right text (but be lenient for function signatures)
    const foundText = normalizedSource.substring(startIndex, startIndex + normalizedSearch.length);
    if (foundText !== normalizedSearch) {
      // Special case: if we added a brace for matching, the found text will have it
      if (matchMethod === 'signature-with-brace' || matchMethod === 'signature-with-brace-no-space') {
        // We're okay, we know the search text is at the start of what we found
        console.log('✅ Function signature match verified (brace added for matching)');
      } else {
        console.warn('🚨 Text mismatch:', {
          expected: normalizedSearch,
          found: foundText,
          startIndex,
          method: matchMethod
        });
        return null;
      }
    }
    
    // Calculate line and column for start position
    const beforeStart = normalizedSource.substring(0, startIndex);
    const startLine = beforeStart.split('\n').length - 1; // 0-based
    const lastLineBeforeStart = beforeStart.split('\n').pop() || '';
    const startColumn = lastLineBeforeStart.length;
    
    // Calculate end position
    const endIndex = startIndex + normalizedSearch.length;
    const beforeEnd = normalizedSource.substring(0, endIndex);
    const endLine = beforeEnd.split('\n').length - 1; // 0-based
    const lastLineBeforeEnd = beforeEnd.split('\n').pop() || '';
    const endColumn = lastLineBeforeEnd.length;
    
    console.log('📍 Position found:', {
      method: matchMethod,
      text: normalizedSearch,
      startIndex,
      endIndex,
      startLine,
      startColumn,
      endLine,
      endColumn,
      foundText,
      normalizedSearchLength: normalizedSearch.length,
      actualTextAtPosition: normalizedSource.substring(startIndex, startIndex + normalizedSearch.length)
    });
    
    console.log('✅ Found text position:', {
      searchText: searchText.substring(0, 30) + '...',
      startLine,
      startColumn,
      endLine,
      endColumn,
      method: startIndex === normalizedSource.indexOf(normalizedSearch) ? 'exact' : 'fuzzy'
    });
    
    return {
      startPoint: { row: startLine, column: startColumn },
      endPoint: { row: endLine, column: endColumn }
    };
  };

  // Helper: Expand around a found pattern to try to match the full search text
  const expandAroundPattern = (sourceCode: string, patternIndex: number, searchText: string, pattern: string) => {
    const searchStart = searchText.indexOf(pattern);
    if (searchStart === -1) return { found: false };
    
    // Calculate where the search text should start if this pattern is part of it
    const expectedStartIndex = patternIndex - searchStart;
    
    if (expectedStartIndex < 0 || expectedStartIndex >= sourceCode.length) {
      return { found: false };
    }
    
    // Check if we can extract the full search text from this position
    const candidateText = sourceCode.substring(expectedStartIndex, expectedStartIndex + searchText.length);
    
    if (candidateText === searchText) {
      return {
        found: true,
        startIndex: expectedStartIndex,
        text: candidateText
      };
    }
    
    // Try expanding character by character to handle whitespace differences
    for (let expansion = 1; expansion <= 10; expansion++) {
      const expandedStart = Math.max(0, expectedStartIndex - expansion);
      const expandedEnd = Math.min(sourceCode.length, expectedStartIndex + searchText.length + expansion);
      const expandedText = sourceCode.substring(expandedStart, expandedEnd);
      
      if (expandedText.includes(searchText)) {
        const searchInExpanded = expandedText.indexOf(searchText);
        return {
          found: true,
          startIndex: expandedStart + searchInExpanded,
          text: searchText
        };
      }
    }
    
    return { found: false };
  };

  // Helper: Calculate highlighting for multi-line selections with robust error handling
  const calculateLineHighlighting = (lineNumber: number, line: string, activeAnchor: any) => {
    try {
      if (!activeAnchor || !activeAnchor.span || !activeAnchor.span.startPoint) {
        return { isPartOfActiveSelection: false, highlightStartCol: 0, highlightEndCol: line.length };
      }

      const startRow = activeAnchor.span.startPoint.row + 1; // Convert to 1-based
      const endRow = activeAnchor.span.endPoint?.row ? activeAnchor.span.endPoint.row + 1 : startRow;
      
      // Validate line numbers
      if (typeof startRow !== 'number' || typeof endRow !== 'number' || startRow < 1 || endRow < 1) {
        console.warn('🚨 Invalid row numbers:', { startRow, endRow, activeAnchor });
        return { isPartOfActiveSelection: false, highlightStartCol: 0, highlightEndCol: line.length };
      }
      
      // Check if current line is within the selection range
      if (lineNumber < startRow || lineNumber > endRow) {
        return { isPartOfActiveSelection: false, highlightStartCol: 0, highlightEndCol: line.length };
      }

      let highlightStartCol = 0;
      let highlightEndCol = line.length;

      // For first line of selection, start from the selection start column
      if (lineNumber === startRow && typeof activeAnchor.span.startPoint.column === 'number') {
        highlightStartCol = Math.max(0, Math.min(activeAnchor.span.startPoint.column, line.length));
      }

      // For last line of selection, end at the selection end column
      if (lineNumber === endRow && activeAnchor.span.endPoint && typeof activeAnchor.span.endPoint.column === 'number') {
        highlightEndCol = Math.max(0, Math.min(activeAnchor.span.endPoint.column, line.length));
      }

      // Ensure valid column bounds and that we have something to highlight
      highlightStartCol = Math.max(0, Math.min(highlightStartCol, line.length));
      highlightEndCol = Math.max(highlightStartCol, Math.min(highlightEndCol, line.length));

      // If start and end are the same, highlight at least one character or whole line if empty
      if (highlightStartCol === highlightEndCol) {
        if (line.length === 0) {
          // Empty line - highlight the whole "line" (just the line break space)
          highlightEndCol = 0;
        } else {
          // Non-empty line - highlight at least one character
          highlightEndCol = Math.min(highlightStartCol + 1, line.length);
        }
      }

      // Debug logging for complex selections (enable with window.__debugHighlighting = true)
      if ((window as any).__debugHighlighting) {
        console.log('🎨 Line highlighting calculated:', {
          lineNumber,
          lineLength: line.length,
          linePreview: line.substring(0, 30) + '...',
          startRow,
          endRow,
          highlightStartCol,
          highlightEndCol,
          highlightText: line.substring(highlightStartCol, highlightEndCol),
          anchorText: activeAnchor.text?.substring(0, 50) + '...'
        });
      }

      return { 
        isPartOfActiveSelection: true, 
        highlightStartCol, 
        highlightEndCol 
      };
      
    } catch (error) {
      console.error('🚨 Error in calculateLineHighlighting:', error, { lineNumber, activeAnchor });
      return { isPartOfActiveSelection: false, highlightStartCol: 0, highlightEndCol: line.length };
    }
  };

  // Helper: Smart enumeration - only find anchors that might match LLM picks
  const enumerateTargetedAnchors = (annotatedCode: any, targetTexts: string[], codeText: string) => {
    console.log('🎯 Smart enumeration for specific targets:', targetTexts);
    
    try {
      // Convert AnnotatedCodeExample.lines to ASTLine[] format that enumerator expects
      if (!annotatedCode?.lines || !Array.isArray(annotatedCode.lines)) {
        console.error('🚨 Invalid annotatedCode structure:', {
          annotatedCode,
          hasLines: !!annotatedCode?.lines,
          linesType: typeof annotatedCode?.lines,
          linesIsArray: Array.isArray(annotatedCode?.lines)
        });
        throw new Error('Invalid annotated code structure');
      }
      
      console.log('🔄 Converting AnnotatedCodeExample to ASTLine format...');
      
      // Convert AnnotatedCodeExample.lines to ASTLine[] format
      const astLines: ASTLine[] = annotatedCode.lines.map((line: any, lineIndex: number) => {
        let charOffset = 0;
        
        const astTokens: ASTToken[] = line.tokens.map((token: any, tokenIndex: number) => {
          const startByte = charOffset;
          const endByte = charOffset + token.text.length;
          charOffset = endByte;
          
          return {
            text: token.text,
            type: token.className || 'unknown', // Use className as type fallback
            startByte,
            endByte,
            startPosition: { row: line.lineNumber - 1, column: startByte },
            endPosition: { row: line.lineNumber - 1, column: endByte },
            nodeId: `line_${lineIndex}_token_${tokenIndex}`,
            className: token.className
          } as ASTToken;
        });
        
        return {
          lineNumber: line.lineNumber,
          tokens: astTokens,
          fullText: line.tokens.map((t: any) => t.text).join('')
        } as ASTLine;
      });
      
      console.log('🎯 Conversion complete:', {
        originalLinesCount: annotatedCode.lines.length,
        convertedLinesCount: astLines.length,
        sampleTokenCount: astLines[0]?.tokens?.length || 0,
        targetTextsCount: targetTexts.length
      });
      
      // Use existing enumeration with converted structure
      const allAnchors = enumerateLensAnchors(astLines, codeText, targetLanguage);
      
      // Filter to only anchors that might match LLM picks
      const relevantAnchors = allAnchors.filter(anchor => 
        targetTexts.some(target => 
          anchor.text.includes(target) || 
          target.includes(anchor.text) ||
          anchor.text.toLowerCase().includes(target.toLowerCase())
        )
      );
      
      // ENHANCED: Create composite anchors for complex expressions
      const compositeAnchors = createCompositeAnchors(allAnchors, targetTexts, codeText);
      
      // Combine both regular and composite anchors
      const combinedAnchors = [...relevantAnchors, ...compositeAnchors];
      
      console.log(`🎯 Enhanced enumeration result: ${allAnchors.length} → ${relevantAnchors.length} relevant + ${compositeAnchors.length} composite = ${combinedAnchors.length} total anchors`);
      
      return combinedAnchors;
    } catch (error) {
      console.error('🚨 Error in enumerateTargetedAnchors:', error);
      throw error;
    }
  };

  // Helper: Create composite anchors for complex expressions like "fn create_user() -> Box<User>"
  const createCompositeAnchors = (allAnchors: LensAnchorDescriptor[], targetTexts: string[], codeText: string) => {
    const compositeAnchors: LensAnchorDescriptor[] = [];
    
    for (const target of targetTexts) {
      // Skip simple targets that are likely to be found directly
      if (target.length < 10 || !target.includes(' ')) continue;
      
      // Try to construct composite anchors for complex expressions
      if (target.startsWith('fn ') && target.includes('->')) {
        // Function signature composite
        const composite = createFunctionSignatureAnchor(allAnchors, target, codeText);
        if (composite) compositeAnchors.push(composite);
        
      } else if (target.includes('::')) {
        // Method call composite like "Box::new(User {"
        const composite = createMethodCallAnchor(allAnchors, target, codeText);
        if (composite) compositeAnchors.push(composite);
        
      } else if (target.includes(':') && !target.includes('::')) {
        // Field definition composite like "name: String"
        const composite = createFieldDefinitionAnchor(allAnchors, target, codeText);
        if (composite) compositeAnchors.push(composite);
      }
    }
    
    return compositeAnchors;
  };

  // Helper: Create function signature composite anchor
  const createFunctionSignatureAnchor = (anchors: LensAnchorDescriptor[], target: string, codeText: string): LensAnchorDescriptor | null => {
    // Extract function name from target like "fn create_user() -> Box<User>"
    const fnMatch = target.match(/fn\s+(\w+)/);
    if (!fnMatch) return null;
    
    const fnName = fnMatch[1];
    
    // Find anchors that might be part of this signature
    const fnKeyword = anchors.find(a => a.text === 'fn');
    const nameAnchor = anchors.find(a => a.text === fnName);
    const arrowAnchor = anchors.find(a => a.text === '->');
    
    if (!fnKeyword || !nameAnchor) return null;
    
    // Calculate the span that covers the entire signature
    const position = findTextPosition(codeText, target);
    if (!position) return null;
    
    return {
      tempId: `composite_fn_${fnName}_${Date.now()}`,
      text: target,
      span: {
        startPoint: position.startPoint,
        endPoint: position.endPoint,
        startByte: 0, // Will be calculated based on position
        endByte: target.length
      },
      nodeType: 'function-signature',
      granularity: 'span',
      features: ['function', 'signature', 'composite'],
      surrounding: {
        before: '',
        after: ''
      },
      confidence: 0.9
    } as LensAnchorDescriptor;
  };

  // Helper: Create method call composite anchor  
  const createMethodCallAnchor = (anchors: LensAnchorDescriptor[], target: string, codeText: string): LensAnchorDescriptor | null => {
    // Extract method pattern from target like "Box::new(User {"
    const methodMatch = target.match(/(\w+)::(\w+)/);
    if (!methodMatch) return null;
    
    const [, className, methodName] = methodMatch;
    
    // Try to find text-based position
    const position = findTextPosition(codeText, target);
    if (!position) return null;
    
    return {
      tempId: `composite_method_${className}_${methodName}_${Date.now()}`,
      text: target,
      span: {
        startPoint: position.startPoint,
        endPoint: position.endPoint,
        startByte: 0,
        endByte: target.length
      },
      nodeType: 'method-call',
      granularity: 'expression',
      features: ['method', 'call', 'composite'],
      surrounding: {
        before: '',
        after: ''
      },
      confidence: 0.85
    } as LensAnchorDescriptor;
  };

  // Helper: Create field definition composite anchor
  const createFieldDefinitionAnchor = (anchors: LensAnchorDescriptor[], target: string, codeText: string): LensAnchorDescriptor | null => {
    // Extract field pattern from target like "name: String"
    const fieldMatch = target.match(/(\w+):\s*(\w+)/);
    if (!fieldMatch) return null;
    
    const [, fieldName, typeName] = fieldMatch;
    
    const position = findTextPosition(codeText, target);
    if (!position) return null;
    
    return {
      tempId: `composite_field_${fieldName}_${Date.now()}`,
      text: target,
      span: {
        startPoint: position.startPoint,
        endPoint: position.endPoint,
        startByte: 0,
        endByte: target.length
      },
      nodeType: 'field-definition',
      granularity: 'declaration',
      features: ['field', 'type', 'composite'],
      surrounding: {
        before: '',
        after: ''
      },
      confidence: 0.8
    } as LensAnchorDescriptor;
  };

  // LEGACY: Old convert step (keeping for reference)
  const convertLLMSelectionsStep = async (): Promise<boolean> => {
    updateZenMeditation('generating', zenMessages.match.emoji, zenMessages.match.message, 85);
    
    const llmSelections = (window as any).__llmSelections;
    startDebugStep('match', { 
      input: {
        llmSelectionsCount: llmSelections?.length || 0,
        availableAnchors: anchors.length,
        llmSelectionsPreview: llmSelections?.slice(0, 3).map((s: any) => ({
          text: s.text?.substring(0, 50),
          novelty: s.novelty
        })),
        llmSelectionsFull: llmSelections, // Include full data for debug panel
        anchorsFull: anchors // Include full anchors data
      }
    });
    
    try {
      if (!llmSelections) {
        throw new Error('No insights to connect');
      }
      
      // Skip AST matching - convert LLM selections directly to AnchorSelections
      // This avoids synthetic anchors and uses LLM's selections as-is
      const directSelections: AnchorSelection[] = llmSelections.map((selection: any, index: number) => ({
        tempId: `llm_${Date.now()}_${index}`,
        text: selection.text,
        novelty: selection.novelty,
        diffType: Array.isArray(selection.diffType) ? selection.diffType : [selection.diffType],
        why: selection.reason,
        granularity: 'span', // LLM selections are usually multi-token spans
        group: 'llm_direct',
        priority: 1
      }));
      
      setSelectedAnchors(directSelections);
      
      // Store in window for immediate access by next step
      (window as any).__directSelections = directSelections;
      console.log('💾 Stored direct selections in window:', directSelections.length);
      
      setOptimizationStats(prev => prev ? { 
        ...prev, 
        matchAccuracy: 1.0 // Direct conversion, no matching needed
      } : null);
      
      completeDebugStep('match', {
        matchedSelectionsCount: directSelections.length,
        exactMatches: directSelections.length,
        syntheticMatches: 0,
        matchAccuracy: 1.0,
        matchedSelections: directSelections
      }, {
        performance: { matchingAlgorithmTime: performance.now() },
        data: {
          inputSelections: llmSelections.length,
          outputMatches: directSelections.length,
          conversionRate: 1.0
        },
        quality: {
          exactMatchRate: 1.0,
          syntheticMatchRate: 0,
          overallMatchingSuccess: true
        }
      });
      
      updateZenMeditation('generating', zenMessages.match.emoji, zenMessages.match.message, 90);
      
      return true;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      errorDebugStep('match', errorMsg);
      updateZenMeditation('input', '⚠️', 'Unable to connect insights to code locations.', 0);
      return false;
    }
  };

  const generateContentStep = async (): Promise<boolean> => {
    updateZenMeditation('live-creation', zenMessages.generate.emoji, zenMessages.generate.message, 95);
    
    const requestId = llmLogger.generateRequestId();
    
    // Use window-stored selections if state is empty (React state timing issue)
    const selectionsForDebug = selectedAnchors.length > 0 
      ? selectedAnchors 
      : (window as any).__directSelections || [];
    
    // Log current state for debugging
    console.log('📊 Generate step state check:', {
      selectedAnchorsLength: selectedAnchors.length,
      windowSelectionsLength: (window as any).__directSelections?.length,
      anchorsLength: anchors.length,
      selectedAnchorsPreview: selectionsForDebug.slice(0, 2)
    });
    
    startDebugStep('generate', {
      input: {
        selectedAnchorsCount: selectionsForDebug.length,
        knownLanguage,
        targetLanguage,
        selectionsPreview: selectionsForDebug.slice(0, 3).map(s => ({
          text: s.text?.substring(0, 50),
          novelty: s.novelty,
          diffType: s.diffType
        }))
      },
      requestId,
      targetGeneration: selectionsForDebug.length
    });
    
    try {
      llmLogger.logRequest({
        requestId,
        component: 'ZenPlaygroundOptimized',
        known: knownLanguage,
        target: targetLanguage,
        endpoint: '/api/lens-anchors/generate',
        anchorCount: selectedAnchors.length,
        payload: {
          picks: selectedAnchors,
          known: knownLanguage,
          target: targetLanguage,
          optimization: 'code-first'
        }
      });
      
      const lenslets: LensletContent[] = [];
      const generationMetrics = {
        successfulTransformations: 0,
        failedTransformations: 0,
        averageContentLength: 0
      };
      
      console.log('🚀 Starting generateLensletContent with:', {
        picksCount: selectedAnchors.length,
        anchorsCount: anchors.length,
        picks: selectedAnchors.slice(0, 2),
        anchors: anchors.slice(0, 2)
      });
      
      // Use window-stored selections if state is empty (React state timing issue)
      const selectionsToUse = selectedAnchors.length > 0 
        ? selectedAnchors 
        : (window as any).__directSelections || [];
      
      console.log('🔍 Selections check:', {
        fromState: selectedAnchors.length,
        fromWindow: (window as any).__directSelections?.length,
        using: selectionsToUse.length
      });
      
      // Check if we have valid selections
      if (!selectionsToUse || selectionsToUse.length === 0) {
        console.error('❌ No selected anchors to generate content for!');
        throw new Error('No selected anchors available');
      }
      
      console.log('📝 Calling generateLensletContent API with', selectionsToUse.length, 'selections');
      
      // Get preferred provider from Zen settings for lens generation
      const preferredProviderForGeneration = getPreferredProvider(providers);
      
      const generateStartTime = Date.now();
      let generatePrompt = '';
      
      await generateLensletContent({
        picks: selectionsToUse,
        anchors: anchors,
        known: knownLanguage,
        target: targetLanguage,
        lensId: selectedLens,
        customInstructions: customLensInstructions,
        provider: preferredProviderForGeneration || undefined,
        onPromptCapture: (prompt, metadata) => {
          console.log('🔍 onPromptCapture called for generate:', {
            hasPrompt: !!prompt,
            promptLength: prompt?.length,
            metadata,
            generateStartTime
          });
          
          generatePrompt = prompt;
          
          // Capture AI Observability Data for lens generation
          const aiObservability: AIObservabilityData = {
            apiCall: {
              endpoint: '/api/lens-anchors/generate',
              method: 'POST',
              timestamp: new Date(generateStartTime).toISOString(),
              requestId: `generate_${generateStartTime}`
            },
            prompt: {
              content: prompt,
              tokenCount: metadata?.tokensUsed?.input,
              parameters: {
                known: knownLanguage,
                target: targetLanguage,
                picks: selectionsToUse.length,
                anchors: anchors.length
              }
            },
            response: {
              content: 'SSE streaming response',
              tokenCount: metadata?.tokensUsed?.output,
              responseTimeMs: Date.now() - generateStartTime,
              statusCode: 200
            },
            metadata: {
              model: 'claude-sonnet-4-20250514',
              provider: 'anthropic',
              cost: metadata?.tokensUsed ? (metadata.tokensUsed.total * 0.003 / 1000) : undefined, // Anthropic pricing
              cacheHit: false
            }
          };
          
          // Store AI observability data for lens generation (accumulate, don't overwrite)
          setAiObservabilityData(prev => {
            const newMap = new Map(prev);
            newMap.set(`generate_${generateStartTime}`, aiObservability);
            console.log('🔍 AI Observability - Stored generate data:', {
              key: `generate_${generateStartTime}`,
              totalEntries: newMap.size,
              keys: Array.from(newMap.keys())
            });
            return newMap;
          });
        },
        onLenslet: (lenslet) => {
          console.log('📦 onLenslet called with:', lenslet);
          lenslets.push(lenslet);
          setGeneratedLenslets([...lenslets]);
          
          // Live lens blooming - create zen lens immediately
          console.log('🌸 Processing lenslet:', lenslet);
          console.log('📍 Available anchors:', anchors.length);
          console.log('🎯 Selected anchors:', selectedAnchors.length);
          
          const newZenLens = transformToZenLenses([lenslet], anchors, selectedAnchors)[0];
          console.log('🎋 Created zen lens:', newZenLens);
          
          if (newZenLens) {
            generationMetrics.successfulTransformations++;
            setZenLenses(prev => {
              const updated = [...prev, newZenLens];
              console.log('🌊 Updated zenLenses:', updated);
              return updated;
            });
            setBloomingLenses(prev => new Set(prev).add(newZenLens.id));
            
            // Remove blooming effect after animation
            setTimeout(() => {
              setBloomingLenses(prev => {
                const next = new Set(prev);
                next.delete(newZenLens.id);
                return next;
              });
            }, 2000);
            
            // Set as active if it's the first lens - ensure immediate interaction
            if (lenslets.length === 1) {
              setActiveLensId(newZenLens.id);
              console.log('🎯 Set active lens:', newZenLens.id);
              // Update active status in the zen lenses array
              setZenLenses(prev => prev.map(lens => ({
                ...lens,
                active: lens.id === newZenLens.id
              })));
            }
          } else {
            generationMetrics.failedTransformations++;
            console.error('❌ Failed to create zen lens from lenslet:', lenslet);
          }
          
          updateZenMeditation('live-creation', '🌺', `Growing lens ${lenslets.length} of ${selectedAnchors.length}...`, 95);
        },
        onComplete: () => {
          console.log('🌙 Generation completed!');
          console.log('📊 Total lenslets generated:', lenslets.length);
          console.log('🎋 Current zenLenses count:', zenLenses.length);
          console.log('🎯 Current active lens:', activeLensId);
          
          // Calculate content quality metrics
          generationMetrics.averageContentLength = lenslets.reduce((acc, l) => {
            const totalLength = (l.lenslet.content.surface?.length || 0) + 
                               (l.lenslet.content.concept?.length || 0) + 
                               (l.lenslet.content.paradigm?.length || 0);
            return acc + totalLength;
          }, 0) / lenslets.length;
          
          llmLogger.logResponse({
            requestId,
            component: 'ZenPlaygroundOptimized',
            known: knownLanguage,
            target: targetLanguage,
            responseTimeMs: performance.now(),
            status: 'success',
            generated: lenslets.length,
            payload: lenslets
          });
          
          completeDebugStep('generate', {
            generatedCount: lenslets.length,
            zenLensesCreated: generationMetrics.successfulTransformations,
            failedTransformations: generationMetrics.failedTransformations,
            averageContentLength: Math.round(generationMetrics.averageContentLength),
            lenslets
          }, {
            performance: {
              totalGenerationTime: performance.now(),
              averageTimePerLens: performance.now() / lenslets.length
            },
            data: {
              targetLenses: selectedAnchors.length,
              actualLenses: lenslets.length,
              completionRate: lenslets.length / selectedAnchors.length
            },
            quality: {
              transformationSuccessRate: generationMetrics.successfulTransformations / lenslets.length,
              contentQualityScore: generationMetrics.averageContentLength / 500, // Normalized to expected content length
              fullPipelineSuccess: lenslets.length === selectedAnchors.length
            }
          });
          
          updateZenMeditation('editing', zenMessages.complete.emoji, zenMessages.complete.message, 100);
          
          // Auto-transition to editing phase after zen lenses are fully processed
          setTimeout(() => {
            console.log('🔄 Transitioning to editing phase...');
            console.log('📊 Final zenLenses check:', zenLenses.length);
            
            setZenPhase(prev => ({ ...prev, phase: 'editing' }));
            
            // Ensure first lens is active for immediate interaction
            if (lenslets.length > 0 && !activeLensId) {
              const firstLensId = lenslets[0].tempId;
              console.log('🎯 Setting first lens as active:', firstLensId);
              setActiveLensId(firstLensId);
              setZenLenses(prev => prev.map(lens => ({
                ...lens,
                active: lens.id === firstLensId
              })));
            }
          }, 2500); // Increased delay to ensure all lenses are processed
        },
        onError: (error) => {
          console.error('❌ Generation error:', error);
          generationMetrics.failedTransformations++;
          throw error;
        }
      });
      
      return true;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      errorDebugStep('generate', errorMsg);
      
      llmLogger.logError({
        requestId,
        component: 'ZenPlaygroundOptimized',
        known: knownLanguage,
        target: targetLanguage,
        error
      });
      
      updateZenMeditation('input', '⚠️', 'Unable to grow explanations. Please try again.', 0);
      return false;
    }
  };

  // Zen unified processing - seamless flow through all phases
  const beginZenGeneration = async () => {
    console.log('🎯 BEGIN DEBUG: beginZenGeneration called!', {
      timestamp: new Date().toISOString(),
      currentPhase: zenPhase.phase,
      codeLength: code.length,
      isGenerating: isGenerating,
      parserReady: parserReady
    });
    
    if (zenPhase.phase !== 'input') {
      console.log('🚨 BEGIN DEBUG: Exiting early - not in input phase!', {
        currentPhase: zenPhase.phase,
        expectedPhase: 'input'
      });
      return;
    }
    
    console.log('🚀 Starting zen generation...');
    setIsGenerating(true);
    setZenLenses([]); // Clear any previous lenses
    setParseResult(null);
    setAnchors([]);
    setSelectedAnchors([]);
    // Clear AI observability data for fresh run
    setAiObservabilityData(new Map());
    console.log('🧹 Cleared previous state and AI observability data');
    
    try {
      // Step 1: Parse
      console.log('🔄 Starting step 1: parse');
      const parseResult = await parseCodeStep();
      if (!parseResult) {
        console.error('💥 Process stopped at step 1: parse');
        setIsGenerating(false);
        return;
      }
      
      console.log('🎯 FLOW DEBUG: Parse result obtained, proceeding to enumerate with result:', {
        hasParseResult: !!parseResult,
        hasAnnotatedCode: !!parseResult?.annotatedCode,
        linesCount: parseResult?.annotatedCode?.lines?.length || 0
      });
      
      // Step 2: LLM Semantic Picks (NEW: LLM first!)
      console.log('🔄 Starting step 2: LLM semantic picks');
      const llmSuccess = await llmSemanticPicksStep(parseResult);
      if (!llmSuccess) {
        console.error('💥 Process stopped at step 2: LLM semantic picks');
        setIsGenerating(false);
        return;
      }
      
      // Wait for state to settle
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Step 3: Validate with AST (NEW: Only enumerate what LLM picked)
      console.log('🔄 Starting step 3: validate with AST');
      const validateSuccess = await validateLLMWithASTStep(parseResult);
      if (!validateSuccess) {
        console.error('💥 Process stopped at step 3: validate');
        setIsGenerating(false);
        return;
      }
      
      // Wait for state to settle longer for critical step
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 5: Generate - verify state before calling
      console.log('🔄 Starting step 5: generate');
      console.log('📊 Pre-generate state check:', {
        selectedAnchorsFromState: selectedAnchors.length,
        anchorsFromState: anchors.length,
        llmSelectionsFromWindow: (window as any).__llmSelections?.length
      });
      
      const generateSuccess = await generateContentStep();
      if (!generateSuccess) {
        console.error('💥 Process stopped at step 5: generate');
        setIsGenerating(false);
        return;
      }
      
    } catch (error) {
      console.error('💥 Error in zen generation:', error);
      updateZenMeditation('input', '⚠️', 'Something went wrong. Please try again.', 0);
    } finally {
      setIsGenerating(false);
    }
  };

  const selectLens = (lensId: string) => {
    setZenLenses(prev => prev.map(lens => ({ ...lens, active: lens.id === lensId })));
    setActiveLensId(lensId);
    
    // Debug: Log lens selection and anchor matching
    const selectedLens = zenLenses.find(l => l.id === lensId);
    const matchingAnchor = selectedLens ? anchors.find(a => a.tempId === selectedLens.tempId) : null;
    const fallbackAnchor = !matchingAnchor && selectedLens && (window as any).__anchorsForHighlighting ? 
      (window as any).__anchorsForHighlighting.find((a: any) => a.tempId === selectedLens.tempId) : null;
    
    console.log('🎯 Lens Selected:', {
      lensId,
      lensTempId: selectedLens?.tempId,
      lensText: selectedLens?.text?.substring(0, 50),
      anchorsCount: anchors.length,
      matchingAnchorFound: !!matchingAnchor,
      fallbackAnchorFound: !!fallbackAnchor,
      usingFallback: !matchingAnchor && !!fallbackAnchor,
      matchingAnchorText: matchingAnchor?.text || fallbackAnchor?.text,
      matchingAnchorSpan: matchingAnchor?.span || fallbackAnchor?.span,
      allAnchorTempIds: anchors.map(a => a.tempId)
    });
    
    // Additional debug: Check what lines should be highlighted
    if (matchingAnchor && matchingAnchor.span && matchingAnchor.span.startPoint) {
      const startRow = matchingAnchor.span.startPoint.row + 1;
      const endRow = matchingAnchor.span.endPoint ? matchingAnchor.span.endPoint.row + 1 : startRow;
      console.log('🎨 Expected Highlighting:', {
        startRow,
        endRow,
        startColumn: matchingAnchor.span.startPoint.column,
        endColumn: matchingAnchor.span.endPoint?.column,
        linesAffected: `Lines ${startRow} to ${endRow}`,
        anchorText: matchingAnchor.text,
        anchorTextLength: matchingAnchor.text?.length
      });
      
      // Check if anchor text matches what should be at this position
      const codeLines = code.split('\n');
      if (codeLines[startRow - 1]) {
        const lineContent = codeLines[startRow - 1];
        const extractedText = lineContent.substring(
          matchingAnchor.span.startPoint.column,
          matchingAnchor.span.endPoint?.column || lineContent.length
        );
        console.log('🔍 Position Verification:', {
          lineContent,
          extractedTextFromSpan: extractedText,
          anchorText: matchingAnchor.text,
          textMatches: extractedText === matchingAnchor.text,
          problemDescription: extractedText !== matchingAnchor.text ? 
            `Span extracts "${extractedText}" but anchor text is "${matchingAnchor.text}"` : 
            'Text matches correctly'
        });
      }
      
      // Enable detailed highlighting debug
      (window as any).__debugHighlighting = true;
    }
  };

  const activeLens = zenLenses.find(l => l.id === activeLensId);

  const updateLensField = (field: string, value: string, subField?: string) => {
    if (!activeLens) return;
    
    setZenLenses(prev => prev.map(lens => {
      if (lens.id !== activeLensId) return lens;
      
      const updated = { ...lens };
      if (subField) {
        (updated as any)[field][subField] = value;
      } else {
        (updated as any)[field] = value;
      }
      return updated;
    }));
  };

  const reset = () => {
    setZenPhase({
      id: 'zen-flow',
      phase: 'input',
      meditation: {
        message: 'Share your code to begin the journey...',
        emoji: '🌱',
        progress: 0
      },
      completedLenses: [],
      generatingLenses: []
    });
    setParseResult(null);
    setAnchors([]);
    setSelectedAnchors([]);
    setGeneratedLenslets([]);
    setZenLenses([]);
    setOptimizationStats(null);
    setActiveLensId('');
    setBloomingLenses(new Set());
    setIsGenerating(false);
    // Clear window storage
    delete (window as any).__llmSelections;
    delete (window as any).__directSelections;
    // Clear AI observability data
    setAiObservabilityData(new Map());
    setDebugSteps(prev => prev.map(step => ({ ...step, status: 'pending', data: undefined, error: undefined })));
  };

  // Unified Zen Interface - always zen, no mode switching
  return (
      <div className="zen-playground">
        {/* Header */}
        <header className="zen-header">
          <div className="header-content">
            <div className="course-context">
              <span className="course-path">{knownLanguage} → {targetLanguage}</span>
              <span className="course-topic">{courseContext.topic}</span>
            </div>
            <div className="zen-meditation">
              <span className="meditation-emoji">{zenPhase.meditation.emoji}</span>
              <span className="meditation-message">{zenPhase.meditation.message}</span>
              {zenPhase.meditation.progress > 0 && (
                <div className="meditation-progress">
                  <div className="progress-bar" style={{ width: `${zenPhase.meditation.progress}%` }}></div>
                </div>
              )}
            </div>
            <div className="session-info">
              {zenLenses.length > 0 && (
                <span className="lens-progress">{zenLenses.length} lenses</span>
              )}
              <div className="auto-save-indicator">
                <span className="save-dot"></span>
                <span className="save-text">Live Creation</span>
              </div>
            </div>
          </div>
        </header>


        {/* Main Zen Layout - Different content based on phase */}
        <main className="zen-layout">
          {zenPhase.phase === 'input' && (
            <section className="zen-input-garden">
              <div className="code-garden-wrapper">
                <h2 className="garden-title">🌱 Code Garden</h2>
                <p className="garden-subtitle">Share your code to discover learning insights...</p>
                
                <div className="input-sections">
                  <div className="language-selection">
                    <div className="language-pair">
                      <label>Known Language</label>
                      <select value={knownLanguage} onChange={(e) => setKnownLanguage(e.target.value)}>
                        <option value="C">C</option>
                        <option value="Python">Python</option>
                        <option value="JavaScript">JavaScript</option>
                      </select>
                    </div>
                    <div className="language-arrow">→</div>
                    <div className="language-pair">
                      <label>Target Language</label>
                      <select value={targetLanguage} onChange={(e) => setTargetLanguage(e.target.value)}>
                        <option value="Rust">Rust</option>
                        <option value="Go">Go</option>
                        <option value="Python">Python</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Language-specific Example Selection */}
                  {knownLanguage === 'C' && (targetLanguage === 'Rust' || targetLanguage === 'Go') && (
                    <div className="example-combo">
                      <label className="example-combo-label">
                        🎯 Test Example:
                        <select 
                          value={selectedExample} 
                          onChange={(e) => selectCodeExample(e.target.value)}
                          className="example-combo-select"
                        >
                          {Object.entries(targetLanguage === 'Go' ? C_TO_GO_EXAMPLES : C_TO_RUST_EXAMPLES).map(([key, example]) => (
                            <option key={key} value={key}>
                              {example.title} - {example.description}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                  
                  {/* Horizontal Lens Selection + Custom Instructions */}
                  <div className="lens-controls-horizontal">
                    <LensPicker
                      lenses={LENS_PICKER_DATA}
                      value={selectedLens}
                      onChange={setSelectedLens}
                      compact={true}
                      className="zen-lens-picker"
                    />
                    <input
                      type="text"
                      value={customLensInstructions}
                      onChange={(e) => setCustomLensInstructions(e.target.value)}
                      placeholder="Custom instructions (optional)..."
                      className="lens-instructions-inline"
                    />
                  </div>
                  
                  <div className="code-input">
                    <textarea
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Paste your code here to begin the journey..."
                      rows={8}
                      className="zen-code-input"
                    />
                  </div>
                  
                  <button 
                    onClick={beginZenGeneration}
                    disabled={isGenerating || !code.trim() || !parserReady}
                    className="zen-generate-button"
                    title={!parserReady ? 'Initializing parser...' : 'Discover insights in your code'}
                  >
                    {!parserReady ? '⏳ Initializing...' : '🌊 Discover Insights'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {(zenPhase.phase === 'generating' || zenPhase.phase === 'live-creation') && (
            <>
              {/* Code Pane */}
              <section className="code-pane">
                <div className="code-wrapper">
                  <div className="code-editor">
                    <pre className="code-content">
                      <code>
                        {code.split('\n').map((line, index) => {
                          const lineNumber = index + 1;
                          
                          // Find the active lens and its corresponding anchor
                          const activeLens = zenLenses.find(lens => lens.id === activeLensId);
                          
                          // Try multiple sources for anchors: state, window fallback, or synthetic
                          let activeAnchor = null;
                          if (activeLens) {
                            // First try state anchors
                            activeAnchor = anchors.find(a => a.tempId === activeLens.tempId);
                            
                            // If not found, try window fallback
                            if (!activeAnchor && (window as any).__anchorsForHighlighting) {
                              const fallbackAnchors = (window as any).__anchorsForHighlighting;
                              activeAnchor = fallbackAnchors.find((a: any) => a.tempId === activeLens.tempId);
                              if (activeAnchor && index === 0) {
                                console.log('⚠️ Using fallback anchor from window storage');
                              }
                            }
                            
                            // If still not found, create synthetic anchor
                            if (!activeAnchor && activeLens.text) {
                              const syntheticSpan = findTextPosition(code, activeLens.text);
                              if (syntheticSpan) {
                                activeAnchor = {
                                  tempId: activeLens.tempId,
                                  text: activeLens.text,
                                  span: syntheticSpan,
                                  synthetic: true
                                };
                                if (index === 0) {
                                  console.log('🔧 Created synthetic anchor for highlighting');
                                }
                              }
                            }
                          }
                          
                          // Debug: Log the matching process (only once, for first line)
                          if (index === 0 && activeLens && (window as any).__debugHighlighting) {
                            console.log('🔍 Highlight Debug:', {
                              location: lineNumber < 500 ? 'First Code Pane' : 'Second Code Pane',
                              activeLensId,
                              activeLensTempId: activeLens.tempId,
                              activeLensText: activeLens.text?.substring(0, 30),
                              anchorsCount: anchors.length,
                              fallbackAnchorsCount: (window as any).__anchorsForHighlighting?.length || 0,
                              anchorsWithMatchingTempId: anchors.filter(a => a.tempId === activeLens.tempId).length,
                              activeAnchorFound: !!activeAnchor,
                              activeAnchorSpan: activeAnchor?.span,
                              firstThreeAnchors: anchors.slice(0, 3).map(a => ({
                                tempId: a.tempId,
                                text: a.text?.substring(0, 30),
                                hasSpan: !!a.span,
                                spanStart: a.span?.startPoint
                              }))
                            });
                          }
                          
                          // Calculate highlighting for this line using the helper function
                          const { isPartOfActiveSelection, highlightStartCol, highlightEndCol } = 
                            calculateLineHighlighting(lineNumber, line, activeAnchor);
                          
                          // Find all anchors that start on this line for clickable areas
                          const anchorsStartingOnLine = anchors.filter(anchor => 
                            anchor.span && anchor.span.startPoint && 
                            anchor.span.startPoint.row + 1 === lineNumber
                          );
                          
                          return (
                            <div key={lineNumber} className="code-line-wrapper">
                              <span className="line-number">{lineNumber}</span>
                              <span className="code-line">
                                {isPartOfActiveSelection ? (
                                  // Highlight the active selection on this line
                                  (() => {
                                    // Debug log what we're about to render
                                    if (lineNumber === 3) {
                                      console.log('🖍️ Rendering highlight for line 3:', {
                                        fullLine: line,
                                        highlightStartCol,
                                        highlightEndCol,
                                        beforeHighlight: line.substring(0, highlightStartCol),
                                        highlightedText: line.substring(highlightStartCol, highlightEndCol),
                                        afterHighlight: line.substring(highlightEndCol)
                                      });
                                    }
                                    return (
                                      <span className="anchor-line">
                                        <span>{line.substring(0, highlightStartCol)}</span>
                                        <span 
                                          className="anchor-highlight active" 
                                          title={`Active: ${activeAnchor?.nodeType || 'selection'}`}
                                        >
                                          {line.substring(highlightStartCol, highlightEndCol)}
                                        </span>
                                        <span>{line.substring(highlightEndCol)}</span>
                                      </span>
                                    );
                                  })()
                                ) : anchorsStartingOnLine.length > 0 ? (
                                  // Show clickable areas for other lenses without highlighting
                                  <span 
                                    className="anchor-line clickable" 
                                    onClick={() => {
                                      // Find the lens for this anchor and select it
                                      const anchor = anchorsStartingOnLine[0];
                                      const lens = zenLenses.find(l => l.tempId === anchor.tempId);
                                      if (lens) selectLens(lens.id);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                    title="Click to view lens"
                                  >
                                    {line}
                                  </span>
                                ) : (
                                  <span>{line}</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </code>
                    </pre>
                  </div>
                  
                  {/* Floating Lens Indicators */}
                  <div className="lens-indicators">
                    {zenLenses.map(lens => (
                      <div 
                        key={lens.id}
                        className={`lens-dot ${lens.active ? 'active' : ''} ${bloomingLenses.has(lens.id) ? 'blooming' : ''}`}
                        title={lens.text}
                        onClick={() => selectLens(lens.id)}
                      />
                    ))}
                  </div>
                </div>
              </section>

              {/* Live Lens Garden */}
              <section className="live-lens-garden">
                <h2 className="garden-title">🎋 Growing Lens Garden</h2>
                
                <div className="lens-blooms">
                  {zenLenses.map((lens) => (
                    <div 
                      key={lens.id} 
                      className={`lens-bloom ${bloomingLenses.has(lens.id) ? 'blooming' : 'complete'} ${lens.active ? 'active' : ''}`}
                      onClick={() => selectLens(lens.id)}
                    >
                      <div className="bloom-header">
                        <span className="bloom-emoji">🌸</span>
                        <span className="bloom-text">{lens.text}</span>
                      </div>
                      
                      {!bloomingLenses.has(lens.id) && (
                        <div className="bloom-preview">
                          <div className="syntax-preview">{lens.syntax.from} → {lens.syntax.to}</div>
                          <div className="concept-preview">{lens.concept.title}</div>
                        </div>
                      )}
                      
                      {bloomingLenses.has(lens.id) && (
                        <div className="bloom-generating">
                          <div className="generating-dots">✨ ✨ ✨</div>
                          <div className="generating-text">Crafting explanation...</div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {zenPhase.phase === 'live-creation' && zenLenses.length < selectedAnchors.length && (
                    <div className="next-bloom">
                      <div className="bloom-placeholder">
                        <span className="placeholder-emoji">🌿</span>
                        <span className="placeholder-text">Next insight growing...</span>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}

          {(zenPhase.phase === 'editing' || (zenPhase.phase === 'live-creation' && zenLenses.length > 0)) && (
            <>
              {/* Code Pane */}
              <section className="code-pane">
                <div className="code-wrapper">
                  <div className="code-editor">
                    <pre className="code-content">
                      <code>
                        {code.split('\n').map((line, index) => {
                          const lineNumber = index + 1;
                          
                          // Find the active lens and its corresponding anchor
                          const activeLens = zenLenses.find(lens => lens.id === activeLensId);
                          
                          // Try multiple sources for anchors: state, window fallback, or synthetic
                          let activeAnchor = null;
                          if (activeLens) {
                            // First try state anchors
                            activeAnchor = anchors.find(a => a.tempId === activeLens.tempId);
                            
                            // If not found, try window fallback
                            if (!activeAnchor && (window as any).__anchorsForHighlighting) {
                              const fallbackAnchors = (window as any).__anchorsForHighlighting;
                              activeAnchor = fallbackAnchors.find((a: any) => a.tempId === activeLens.tempId);
                              if (activeAnchor && index === 0) {
                                console.log('⚠️ Using fallback anchor from window storage');
                              }
                            }
                            
                            // If still not found, create synthetic anchor
                            if (!activeAnchor && activeLens.text) {
                              const syntheticSpan = findTextPosition(code, activeLens.text);
                              if (syntheticSpan) {
                                activeAnchor = {
                                  tempId: activeLens.tempId,
                                  text: activeLens.text,
                                  span: syntheticSpan,
                                  synthetic: true
                                };
                                if (index === 0) {
                                  console.log('🔧 Created synthetic anchor for highlighting');
                                }
                              }
                            }
                          }
                          
                          // Debug: Log the matching process (only once, for first line)
                          if (index === 0 && activeLens && (window as any).__debugHighlighting) {
                            console.log('🔍 Highlight Debug:', {
                              location: lineNumber < 500 ? 'First Code Pane' : 'Second Code Pane',
                              activeLensId,
                              activeLensTempId: activeLens.tempId,
                              activeLensText: activeLens.text?.substring(0, 30),
                              anchorsCount: anchors.length,
                              fallbackAnchorsCount: (window as any).__anchorsForHighlighting?.length || 0,
                              anchorsWithMatchingTempId: anchors.filter(a => a.tempId === activeLens.tempId).length,
                              activeAnchorFound: !!activeAnchor,
                              activeAnchorSpan: activeAnchor?.span,
                              firstThreeAnchors: anchors.slice(0, 3).map(a => ({
                                tempId: a.tempId,
                                text: a.text?.substring(0, 30),
                                hasSpan: !!a.span,
                                spanStart: a.span?.startPoint
                              }))
                            });
                          }
                          
                          // Calculate highlighting for this line using the helper function
                          const { isPartOfActiveSelection, highlightStartCol, highlightEndCol } = 
                            calculateLineHighlighting(lineNumber, line, activeAnchor);
                          
                          // Find all anchors that start on this line for clickable areas
                          const anchorsStartingOnLine = anchors.filter(anchor => 
                            anchor.span && anchor.span.startPoint && 
                            anchor.span.startPoint.row + 1 === lineNumber
                          );
                          
                          return (
                            <div key={lineNumber} className="code-line-wrapper">
                              <span className="line-number">{lineNumber}</span>
                              <span className="code-line">
                                {isPartOfActiveSelection ? (
                                  // Highlight the active selection on this line
                                  (() => {
                                    // Debug log what we're about to render
                                    if (lineNumber === 3) {
                                      console.log('🖍️ Rendering highlight for line 3:', {
                                        fullLine: line,
                                        highlightStartCol,
                                        highlightEndCol,
                                        beforeHighlight: line.substring(0, highlightStartCol),
                                        highlightedText: line.substring(highlightStartCol, highlightEndCol),
                                        afterHighlight: line.substring(highlightEndCol)
                                      });
                                    }
                                    return (
                                      <span className="anchor-line">
                                        <span>{line.substring(0, highlightStartCol)}</span>
                                        <span 
                                          className="anchor-highlight active" 
                                          title={`Active: ${activeAnchor?.nodeType || 'selection'}`}
                                        >
                                          {line.substring(highlightStartCol, highlightEndCol)}
                                        </span>
                                        <span>{line.substring(highlightEndCol)}</span>
                                      </span>
                                    );
                                  })()
                                ) : anchorsStartingOnLine.length > 0 ? (
                                  // Show clickable areas for other lenses without highlighting
                                  <span 
                                    className="anchor-line clickable" 
                                    onClick={() => {
                                      // Find the lens for this anchor and select it
                                      const anchor = anchorsStartingOnLine[0];
                                      const lens = zenLenses.find(l => l.tempId === anchor.tempId);
                                      if (lens) selectLens(lens.id);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                    title="Click to view lens"
                                  >
                                    {line}
                                  </span>
                                ) : (
                                  <span>{line}</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </code>
                    </pre>
                  </div>
                  
                  {/* Floating Lens Indicators */}
                  <div className="lens-indicators">
                    {zenLenses.map(lens => (
                      <div 
                        key={lens.id}
                        className={`lens-dot ${lens.active ? 'active' : ''}`}
                        title={lens.text}
                        onClick={() => selectLens(lens.id)}
                      />
                    ))}
                  </div>
                </div>
              </section>

          {/* Content Pane */}
          <section className="content-pane">
            
            {zenLenses.length === 0 && (
              <div className="zen-empty-state">
                <div className="empty-icon">🌱</div>
                <div className="empty-title">Lens Garden Growing...</div>
                <div className="empty-subtitle">Your explanations are being crafted with care</div>
              </div>
            )}
            
            {zenLenses.length > 0 && !activeLens && (
              <div className="zen-welcome-state">
                <div className="welcome-icon">🎋</div>
                <div className="welcome-title">Your Lens Garden is Ready</div>
                <div className="welcome-subtitle">Click on any highlighted code to explore the explanation</div>
                <div className="lens-preview-list">
                  {zenLenses.slice(0, 3).map((lens, index) => (
                    <div 
                      key={lens.id}
                      className="lens-preview-item"
                      onClick={() => selectLens(lens.id)}
                    >
                      <span className="preview-number">{index + 1}</span>
                      <span className="preview-text">{lens.text}</span>
                      <span className="preview-line">Line {lens.line}</span>
                    </div>
                  ))}
                  {zenLenses.length > 3 && (
                    <div className="more-lenses">+{zenLenses.length - 3} more lenses</div>
                  )}
                </div>
              </div>
            )}
            
            {activeLens && (
              <>
                {/* Current Editing Context */}

                {/* Lens Header with Navigation */}
                <div className="lens-header">
                  <div className="lens-navigation-header">
                    <button 
                      className="nav-button prev" 
                      disabled={zenLenses.findIndex(l => l.id === activeLensId) === 0}
                      onClick={() => {
                        const currentIndex = zenLenses.findIndex(l => l.id === activeLensId);
                        if (currentIndex > 0) {
                          selectLens(zenLenses[currentIndex - 1].id);
                        }
                      }}
                      title="Previous lens"
                    >
                      ‹
                    </button>
                    <span className="nav-info">
                      {zenLenses.findIndex(l => l.id === activeLensId) + 1} of {zenLenses.length}
                    </span>
                    <button 
                      className="nav-button next"
                      disabled={zenLenses.findIndex(l => l.id === activeLensId) === zenLenses.length - 1}
                      onClick={() => {
                        const currentIndex = zenLenses.findIndex(l => l.id === activeLensId);
                        if (currentIndex < zenLenses.length - 1) {
                          selectLens(zenLenses[currentIndex + 1].id);
                        }
                      }}
                      title="Next lens"
                    >
                      ›
                    </button>
                  </div>
                </div>

                {/* Lens Levels */}
                <div className="lens-levels">
                  {/* Debug Info Toggle and Display */}
                  <div className="highlight-debug-section">
                    <div className="debug-toggle">
                      <label className="toggle-switch">
                        <input 
                          type="checkbox" 
                          checked={showHighlightDebug}
                          onChange={(e) => setShowHighlightDebug(e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className="toggle-label">Show Highlight Debug Info</span>
                    </div>
                    
                    {showHighlightDebug && (() => {
                      // Find the active anchor for debug info
                      const activeAnchor = anchors.find(a => a.tempId === activeLens.tempId) || 
                        (window as any).__anchorsForHighlighting?.find((a: any) => a.tempId === activeLens.tempId);
                      
                      return (
                        <div className="debug-info-panel">
                          <div className="debug-header">🔍 Highlight Position Debug</div>
                          
                          <div className="debug-grid">
                            <div className="debug-item">
                              <span className="debug-label">Lens ID:</span>
                              <span className="debug-value">{activeLens.id}</span>
                            </div>
                            
                            <div className="debug-item">
                              <span className="debug-label">Temp ID:</span>
                              <span className="debug-value">{activeLens.tempId}</span>
                            </div>
                            
                            <div className="debug-item">
                              <span className="debug-label">Line Number:</span>
                              <span className="debug-value">{activeLens.line}</span>
                            </div>
                            
                            <div className="debug-item">
                              <span className="debug-label">Anchor Found:</span>
                              <span className={`debug-value ${activeAnchor ? 'success' : 'error'}`}>
                                {activeAnchor ? '✅ Yes' : '❌ No'}
                              </span>
                            </div>
                            
                            {activeAnchor && (
                              <>
                                <div className="debug-item">
                                  <span className="debug-label">Anchor Source:</span>
                                  <span className="debug-value">
                                    {anchors.find(a => a.tempId === activeLens.tempId) ? 'State' : 'Window Fallback'}
                                  </span>
                                </div>
                                
                                <div className="debug-item">
                                  <span className="debug-label">Has Span:</span>
                                  <span className={`debug-value ${activeAnchor.span ? 'success' : 'error'}`}>
                                    {activeAnchor.span ? '✅ Yes' : '❌ No'}
                                  </span>
                                </div>
                                
                                {activeAnchor.span && (
                                  <>
                                    <div className="debug-item full-width">
                                      <span className="debug-label">Start Position:</span>
                                      <span className="debug-value">
                                        Row: {activeAnchor.span.startPoint.row}, Col: {activeAnchor.span.startPoint.column}
                                      </span>
                                    </div>
                                    
                                    <div className="debug-item full-width">
                                      <span className="debug-label">End Position:</span>
                                      <span className="debug-value">
                                        Row: {activeAnchor.span.endPoint?.row ?? activeAnchor.span.startPoint.row}, 
                                        Col: {activeAnchor.span.endPoint?.column ?? activeAnchor.span.startPoint.column}
                                      </span>
                                    </div>
                                    
                                    <div className="debug-item full-width">
                                      <span className="debug-label">Highlight Lines:</span>
                                      <span className="debug-value">
                                        {activeAnchor.span.startPoint.row + 1} - {(activeAnchor.span.endPoint?.row ?? activeAnchor.span.startPoint.row) + 1}
                                      </span>
                                    </div>
                                  </>
                                )}
                                
                                <div className="debug-item full-width">
                                  <span className="debug-label">Text (first 50 chars):</span>
                                  <span className="debug-value code">{activeAnchor.text.substring(0, 50)}{activeAnchor.text.length > 50 ? '...' : ''}</span>
                                </div>
                                
                                <div className="debug-item">
                                  <span className="debug-label">Is Synthetic:</span>
                                  <span className="debug-value">{(activeAnchor as any).synthetic ? '⚠️ Yes' : '✓ No'}</span>
                                </div>
                              </>
                            )}
                            
                            {!activeAnchor && (
                              <div className="debug-warning">
                                ⚠️ No anchor found for this lens! Highlighting won't work.
                                <br />
                                Check: anchors.length = {anchors.length}, 
                                window.__anchorsForHighlighting = {(window as any).__anchorsForHighlighting?.length ?? 0}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Level 1: Syntax */}
                  <div className="lens-level level-1">
                    <div className="level-header">
                      <span className="level-indicator">1</span>
                      <span className="level-name">Syntax</span>
                    </div>
                    <div className="level-content">
                      <div className="syntax-mapping">
                        <div className="syntax-from">
                          <span className="syntax-label">{knownLanguage}</span>
                          <input 
                            className="syntax-input readonly" 
                            value={activeLens.syntax.from} 
                            readOnly 
                          />
                        </div>
                        <div className="syntax-arrow">→</div>
                        <div className="syntax-to">
                          <span className="syntax-label">{targetLanguage}</span>
                          <input 
                            className="syntax-input editable" 
                            value={activeLens.syntax.to}
                            onChange={(e) => updateLensField('syntax', e.target.value, 'to')}
                            placeholder="Target syntax..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Level 2: Concept */}
                  <div className="lens-level level-2">
                    <div className="level-header">
                      <span className="level-indicator">2</span>
                      <span className="level-name">Concept</span>
                    </div>
                    <div className="level-content">
                      <div className="content-display">
                        <p className="content-text">{activeLens.concept.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Level 3: Paradigm */}
                  <div className="lens-level level-3">
                    <div className="level-header">
                      <span className="level-indicator">3</span>
                      <span className="level-name">Paradigm</span>
                    </div>
                    <div className="level-content">
                      <div className="content-display">
                        <p className="content-text">{activeLens.paradigm.explanation}</p>
                      </div>
                    </div>
                  </div>
                </div>


              </>
            )}
          </section>
            </>
          )}
        </main>

        {/* Enhanced Debug Panel */}
        <DebugPanelEnhanced
          isOpen={debugPanelVisible}
          onClose={() => setDebugPanelVisible(false)}
          debugSteps={debugSteps}
          optimizationStats={optimizationStats}
          currentState={{
            phase: zenPhase.phase,
            anchorsCount: anchors.length,
            selectedAnchorsCount: selectedAnchors.length,
            zenLensesCount: zenLenses.length,
            activeLensId: activeLensId || undefined,
            isGenerating
          }}
          aiObservabilityData={aiObservabilityData}
        />

        {/* Old Debug Panel - Commented out for reference */}
        {false && debugPanelVisible && (
          <div className="debug-panel">
            <div className="debug-header">
              <h3>🔍 Debug Analysis</h3>
              <button 
                className="debug-close"
                onClick={() => setDebugPanelVisible(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="debug-content">
              {/* Process Steps Overview */}
              <div className="debug-section">
                <h4>Processing Pipeline</h4>
                <div className="debug-steps-grid">
                  {debugSteps.map((step, index) => (
                    <div key={step.id} className={`debug-step ${step.status}`}>
                      <div className="step-header">
                        <span className="step-number">{index + 1}</span>
                        <span className="step-name">{step.name}</span>
                        <span className={`step-status ${step.status}`}>
                          {step.status === 'completed' && '✓'}
                          {step.status === 'running' && '⏳'}
                          {step.status === 'error' && '❌'}
                          {step.status === 'pending' && '○'}
                        </span>
                      </div>
                      
                      {step.duration && (
                        <div className="step-timing">
                          {Math.round(step.duration)}ms
                        </div>
                      )}
                      
                      {step.error && (
                        <div className="step-error">
                          <strong>Error:</strong> {step.error}
                        </div>
                      )}
                      
                      {step.analytics && (
                        <div className="step-analytics">
                          {step.analytics.performance && (
                            <div className="analytics-section">
                              <strong>Performance:</strong>
                              <pre>{JSON.stringify(step.analytics.performance, null, 2)}</pre>
                            </div>
                          )}
                          {step.analytics.data && (
                            <div className="analytics-section">
                              <strong>Data:</strong>
                              <pre>{JSON.stringify(step.analytics.data, null, 2)}</pre>
                            </div>
                          )}
                          {step.analytics.quality && (
                            <div className="analytics-section">
                              <strong>Quality:</strong>
                              <pre>{JSON.stringify(step.analytics.quality, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {step.output && (
                        <details className="step-details">
                          <summary>Output Data</summary>
                          <pre className="debug-json">{JSON.stringify(step.output, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Optimization Analytics */}
              {optimizationStats && (
                <div className="debug-section">
                  <h4>Optimization Analytics</h4>
                  <div className="optimization-debug">
                    <div className="metric">
                      <span className="metric-label">Token Savings:</span>
                      <span className="metric-value">{optimizationStats.tokensSaved}</span>
                      <span className="metric-percentage">({Math.round((optimizationStats.tokensSaved / 612) * 100)}%)</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Original Tokens:</span>
                      <span className="metric-value">{optimizationStats.originalTokens}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Optimized Tokens:</span>
                      <span className="metric-value">{optimizationStats.optimizedTokens}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Response Time:</span>
                      <span className="metric-value">{Math.round(optimizationStats.responseTime)}ms</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Match Accuracy:</span>
                      <span className="metric-value">{Math.round(optimizationStats.matchAccuracy * 100)}%</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Current State */}
              <div className="debug-section">
                <h4>Current State</h4>
                <div className="state-debug">
                  <div className="state-item">
                    <strong>Phase:</strong> {zenPhase.phase}
                  </div>
                  <div className="state-item">
                    <strong>Anchors Found:</strong> {anchors.length}
                  </div>
                  <div className="state-item">
                    <strong>Selected Anchors:</strong> {selectedAnchors.length}
                  </div>
                  <div className="state-item">
                    <strong>Zen Lenses:</strong> {zenLenses.length}
                  </div>
                  <div className="state-item">
                    <strong>Active Lens:</strong> {activeLensId || 'None'}
                  </div>
                  <div className="state-item">
                    <strong>Is Generating:</strong> {isGenerating ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
              
              {/* AI Debug Analysis */}
              <div className="debug-section">
                <h4>🤖 AI Debug Analysis</h4>
                <div className="debug-controls">
                  <label className="debug-toggle">
                    <input 
                      type="checkbox" 
                      checked={aiDebugEnabled} 
                      onChange={(e) => setAiDebugEnabled(e.target.checked)}
                    />
                    AI Analysis
                  </label>
                  <label className="debug-toggle">
                    <input 
                      type="checkbox" 
                      checked={realTimeMonitoring} 
                      onChange={(e) => setRealTimeMonitoring(e.target.checked)}
                    />
                    Real-time Monitor
                  </label>
                  <label className="debug-toggle">
                    <input 
                      type="checkbox" 
                      checked={autoDebug} 
                      onChange={(e) => setAutoDebug(e.target.checked)}
                    />
                    Auto Debug
                  </label>
                </div>
                
                {debugAnalyses.length > 0 && (
                  <div className="debug-analyses">
                    <h5>Recent Analyses ({debugAnalyses.length})</h5>
                    {debugAnalyses.slice(-5).map((analysis, index) => (
                      <div key={index} className={`analysis-item severity-${analysis.severity}`}>
                        <div className="analysis-header">
                          <span className="analysis-step">{analysis.step}</span>
                          <span className={`analysis-severity ${analysis.severity}`}>
                            {analysis.severity === 'critical' && '🔴'}
                            {analysis.severity === 'high' && '🟠'}
                            {analysis.severity === 'medium' && '🟡'}
                            {analysis.severity === 'low' && '🟢'}
                          </span>
                          <span className="analysis-time">
                            {new Date(analysis.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="analysis-issue">
                          <strong>Issue:</strong> {analysis.issue}
                        </div>
                        <div className="analysis-recommendation">
                          <strong>Recommendation:</strong> {analysis.recommendation}
                        </div>
                        {analysis.evidence && (
                          <details className="analysis-evidence">
                            <summary>Evidence</summary>
                            <pre>{JSON.stringify(analysis.evidence, null, 2)}</pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Network Traffic Monitor */}
              <div className="debug-section">
                <h4>🌐 Network Monitor</h4>
                {networkCalls.length > 0 ? (
                  <div className="network-calls">
                    <div className="network-stats">
                      <span className="stat">
                        Total: {networkCalls.length}
                      </span>
                      <span className="stat success">
                        Success: {networkCalls.filter(c => c.status && c.status < 400).length}
                      </span>
                      <span className="stat error">
                        Failed: {networkCalls.filter(c => !c.status || c.status >= 400).length}
                      </span>
                      <span className="stat info">
                        WASM: {networkCalls.filter(c => c.url.includes('wasm') || c.url.includes('tree-sitter')).length}
                      </span>
                    </div>
                    <div className="network-list">
                      {networkCalls
                        .filter(call => !call.url.includes('wasm') && !call.url.includes('tree-sitter'))
                        .slice(-5).map((call, index) => (
                        <div key={call.id} className={`network-call ${call.status && call.status >= 400 ? 'failed' : 'success'}`}>
                          <div className="call-header">
                            <span className="call-method">{call.method}</span>
                            <span className="call-url">{call.url}</span>
                            <span className={`call-status ${call.status && call.status >= 400 ? 'error' : 'success'}`}>
                              {call.status || 'pending'}
                            </span>
                          </div>
                          {call.responseTime && (
                            <div className="call-timing">
                              {Math.round(call.responseTime - call.requestTime)}ms
                            </div>
                          )}
                          {call.error && (
                            <div className="call-error">
                              <strong>Error:</strong> {call.error}
                            </div>
                          )}
                          <details className="call-details">
                            <summary>Request/Response</summary>
                            {call.requestBody && (
                              <div className="call-request">
                                <strong>Request:</strong>
                                <pre>{typeof call.requestBody === 'string' ? call.requestBody : JSON.stringify(call.requestBody, null, 2)}</pre>
                              </div>
                            )}
                            {call.responseBody && (
                              <div className="call-response">
                                <strong>Response:</strong>
                                <pre>{call.responseBody.substring(0, 500)}{call.responseBody.length > 500 ? '...' : ''}</pre>
                              </div>
                            )}
                          </details>
                        </div>
                      ))}
                    </div>
                    
                    {/* WASM/Binary requests section */}
                    {networkCalls.filter(c => c.url.includes('wasm') || c.url.includes('tree-sitter')).length > 0 && (
                      <details className="binary-requests-section">
                        <summary style={{ fontSize: '13px', color: 'var(--zen-text-muted)', marginTop: '12px' }}>
                          🔧 WASM/Binary Requests ({networkCalls.filter(c => c.url.includes('wasm') || c.url.includes('tree-sitter')).length})
                        </summary>
                        <div className="binary-network-list">
                          {networkCalls
                            .filter(c => c.url.includes('wasm') || c.url.includes('tree-sitter'))
                            .slice(-3).map((call, index) => (
                            <div key={call.id} className="network-call binary-call">
                              <div className="call-header">
                                <span className="call-method">{call.method}</span>
                                <span className="call-url">
                                  {call.url.split('/').pop() || call.url}
                                </span>
                                <span className={`call-status ${call.status && call.status >= 400 ? 'error' : 'success'}`}>
                                  {call.status || 'pending'}
                                </span>
                              </div>
                              {call.responseTime && (
                                <div className="call-timing">
                                  {Math.round(call.responseTime - call.requestTime)}ms
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                ) : (
                  <div className="no-network-data">
                    No network calls detected yet
                  </div>
                )}
              </div>
              
              {/* Real-time State Monitor */}
              <div className="debug-section">
                <h4>📊 State Timeline</h4>
                {stateSnapshots.length > 0 ? (
                  <div className="state-timeline">
                    <div className="timeline-stats">
                      <span className="stat">Snapshots: {stateSnapshots.length}</span>
                      <span className="stat">Memory: {stateSnapshots[stateSnapshots.length - 1]?.memoryUsage ? 
                        Math.round(stateSnapshots[stateSnapshots.length - 1].memoryUsage.usedJSHeapSize / 1024 / 1024) + 'MB' : 
                        'N/A'
                      }</span>
                    </div>
                    <div className="timeline-chart">
                      {stateSnapshots.slice(-10).map((snapshot, index) => (
                        <div key={index} className="timeline-item">
                          <div className="timeline-time">
                            {new Date(snapshot.timestamp).toLocaleTimeString()}
                          </div>
                          <div className="timeline-step">{snapshot.step}</div>
                          <div className="timeline-state">
                            <span className="state-metric">
                              A:{snapshot.state.anchorsFound}
                            </span>
                            <span className="state-metric">
                              S:{snapshot.state.selectedAnchors}
                            </span>
                            <span className="state-metric">
                              L:{snapshot.state.zenLenses}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-timeline-data">
                    No state snapshots captured yet
                  </div>
                )}
              </div>
              
              {/* Export Debug Data */}
              <div className="debug-section">
                <h4>Debug Export</h4>
                <button 
                  className="debug-export-btn"
                  onClick={() => {
                    const debugData = {
                      timestamp: new Date().toISOString(),
                      debugSteps,
                      optimizationStats,
                      currentState: {
                        phase: zenPhase.phase,
                        anchorsCount: anchors.length,
                        selectedAnchorsCount: selectedAnchors.length,
                        zenLensesCount: zenLenses.length,
                        activeLensId,
                        isGenerating
                      },
                      anchors: anchors.slice(0, 5), // First 5 for brevity
                      selectedAnchors: selectedAnchors.slice(0, 5),
                      zenLenses: zenLenses.slice(0, 3),
                      // AI Debug Data
                      aiDebugAnalyses: debugAnalyses,
                      networkCalls: networkCalls.slice(-20),
                      stateSnapshots: stateSnapshots.slice(-20),
                      debugConfig: {
                        aiDebugEnabled,
                        realTimeMonitoring,
                        autoDebug,
                        debugMode
                      },
                      systemInfo: {
                        userAgent: navigator.userAgent,
                        memory: (performance as any).memory ? {
                          usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
                          totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
                          jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit
                        } : undefined,
                        connection: (navigator as any).connection ? {
                          effectiveType: (navigator as any).connection.effectiveType,
                          downlink: (navigator as any).connection.downlink,
                          rtt: (navigator as any).connection.rtt
                        } : undefined
                      }
                    };
                    
                    const dataStr = JSON.stringify(debugData, null, 2);
                    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                    const exportFileDefaultName = `zen-debug-${Date.now()}.json`;
                    
                    const linkElement = document.createElement('a');
                    linkElement.setAttribute('href', dataUri);
                    linkElement.setAttribute('download', exportFileDefaultName);
                    linkElement.click();
                  }}
                >
                  📊 Export Debug Data
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Floating Action Menu (FAB) - Always Accessible */}
        <div className="actions-menu">
          <button 
            ref={actionsToggleRef}
            className={`actions-toggle ${actionsMenuExpanded ? 'expanded' : ''}`}
            onClick={() => setActionsMenuExpanded(!actionsMenuExpanded)}
            title="Quick Actions"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="4" cy="10" r="1.5" fill="currentColor"/>
              <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
              <circle cx="16" cy="10" r="1.5" fill="currentColor"/>
            </svg>
          </button>
          
          {actionsMenuExpanded && (
            <div 
              ref={actionsDropdownRef}
              className={`actions-dropdown actions-dropdown-${menuPosition}`}
            >
              <button 
                className="action-item" 
                title="Toggle Debug Panel"
                onClick={() => {
                  setDebugPanelVisible(!debugPanelVisible);
                  setActionsMenuExpanded(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M7 10L9 8L7 6M4 12C4 13.66 5.34 15 7 15H12C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1H7C5.34 1 4 2.34 4 4V12Z" stroke="currentColor" strokeWidth="1.2"/>
                  <path d="M1 4H4V12H1" stroke="currentColor" strokeWidth="1.2"/>
                </svg>
                Debug Panel
              </button>
              
              <button 
                className="action-item" 
                title="Export Lenses"
                onClick={() => {
                  const dataStr = JSON.stringify(zenLenses, null, 2);
                  const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                  const exportFileDefaultName = 'zen-lenses.json';
                  
                  const linkElement = document.createElement('a');
                  linkElement.setAttribute('href', dataUri);
                  linkElement.setAttribute('download', exportFileDefaultName);
                  linkElement.click();
                  setActionsMenuExpanded(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 14L14 14M8 2V10M5 7L8 10L11 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
                Export Lenses
              </button>
              
              <button 
                className="action-item" 
                title="Reset & Start Over"
                onClick={() => {
                  reset();
                  setActionsMenuExpanded(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 6C3 8.76 5.24 11 8 11C9.55 11 10.93 10.31 11.86 9.25M13 10C13 7.24 10.76 5 8 5C6.45 5 5.07 5.69 4.14 6.75M1 3L3 3L3 5M15 13L13 13L13 11" stroke="currentColor" strokeWidth="1.2"/>
                </svg>
                Reset
              </button>
            </div>
          )}
        </div>

      </div>
    );
};

// Export with Error Boundary wrapper
export const ZenCreator: React.FC = () => {
  return (
    <DebugErrorBoundary>
      <ZenPlaygroundCore />
    </DebugErrorBoundary>
  );
};
